import { Injectable, NgModule } from '@angular/core';
import { DataApiService, BaseService } from '@xpo-ltl/data-api';
import { ConfigManagerService, CONFIG_MANAGER_SERVICE_CONFIGURATION, ConfigManagerModule } from '@xpo-ltl/config-manager';
import { PayloadType } from '@xpo-ltl/sdk-common';

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes} checked by tsc
 */
import * as ɵngcc0 from '@angular/core';
import * as ɵngcc1 from '@xpo-ltl/data-api';
import * as ɵngcc2 from '@xpo-ltl/config-manager';
class LocationApiService extends BaseService {
    /**
     * @param {?} dataApiService
     * @param {?} configManager
     */
    constructor(dataApiService, configManager) {
        super();
        this.dataApiService = dataApiService;
        this.configManager = configManager;
    }
    /**
     * Health check URL. Responds with success message if the service is running.
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    healthCheck(dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/health-check'), {}, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * List of resources.
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    options(dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/options'), {}, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * Details of user invoking the API.
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    loggedInUser(dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/appusers/logged-in-user'), {}, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation calculates transit days and provides the latest pickup date or the earliest delivery date for the given origin and destination postal codes.
     * <br/>
     * <br/>Use Case 1
     * <br/>Pre-condition: Valid origin and destination postal codes must be provided, but requested pickup and delivery dates are not.
     * <br/>Post-condition: The requested pickup date is defaulted to today and the estimated delivery date, transit days, and guaranteed indicator will be returned.
     * <br/>
     * <br/>Use Case 2
     * <br/>Pre-condition: Valid origin and destination postal codes along with the requested pickup date are provided, but the delivery date is not.
     * <br/>Post-condition: The estimated delivery date is provided along with the transit days and guaranteed indicator.
     * <br/>
     * <br/>Use Case 3
     * <br/>Pre-condition: Valid origin and destination postal codes along with the requested delivery date are provided, but the pickup date is not.
     * <br/>Post-condition: The latest pickup date along with the transit days and guaranteed indicator are returned.
     * <br/>
     * <br/>Use Case 4
     * <br/>Pre-condition: The necessary input isn't provided or is invalid.
     * <br/>Post-condition: An appropriate error message will be returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    calculateTransitTime(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/transit-times'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation deletes existing Location Feature Setting(s) that have either been deprecated or are not needed.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid feature code and sicCd OR a list of instIds for existing Location Feature Setting(s) must be provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the Location Feature Setting is deleted.
     * <br/>2. Otherwise an illegal argument exception is thrown, such as a 404 - Not Found if the feature code is not found.  NoResultException should be handled with a NotFoundException from the jaxrs.
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    deleteLocationFeatureSettings(queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/location-feature-settings'), {
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.delete(apiRequest);
    }
    /**
     * This operation determines if the requested date is a non-work day, as well as its type, for the location provided.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid sicCd and date must be provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If the date provided is a non-work day, then its type will be set in nonWorkDayTypeCd.  The description for the non-work day along with an indicator for whether Linehaul runs on that day will also be returned.
     * <br/>2. If the date provided isn't a non-work day, then nonWorkDayTypeCd will be null.
     * <br/>3. Otherwise an appropriate error message will be returned.
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    determineNonWorkDay(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/service-centers/{sicCd}/calendar-dates/{date}/non-work-day'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation determines the operational service date for a lane (origin to destination) based on the calendar date and offset provided.
     * <br/>	When not provided, calendar date defaults to the current date and offset defaults to 0. Passing an offset of 0 allows for checking whether the calendar date is a service date.
     * <br/>	Passing findNextServiceDateInd allows for checking for the next service date from the offset of 0.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. A valid origin and destination SIC must be provided.
     * <br/>2. The calendar date defaults to the current date and offset defaults to 0 if they aren't provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the service date is returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} pathParams
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    determineOperationalServiceDate(pathParams, queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/origins/{originSicCd}/destinations/{destinationSicCd}/service-date'), {
            pathParams: pathParams,
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * Returns geo area information about a location.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. The SIC code and type code are required.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If a geo area is found for the SIC and type provided, then information about it will be returned.
     * <br/>2. Otherwise an appropriate error message will be returned.
     * @param {?} pathParams
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getGeoAreasForLocation(pathParams, queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/service-centers/{sicCd}/areas/{polygonTypeCd}'), {
            pathParams: pathParams,
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation retrieves the host SIC for the requested zone SIC.
     * <br/>
     * <br/>Mandatory Criteria:
     * <br/>- Zone SIC
     * <br/>
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getHostSicDetails(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/service-centers/host/{sicCd}'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation returns location reference information for a SIC code or location instance ID.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid sicCd or instanceId is provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the location reference details are returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getLocationReferenceDetails(queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/loc-reference-details'), {
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation returns location hierarchy group information for a SIC.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid sicCd and at least one hierarchyGroupCategoryCd must be provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the location hierarchy group and location reference details are returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getLocHierarchyGroup(queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/service-centers/hierarchy-group'), {
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation retrieves the operations service center profitability for a SIC.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid zone SIC must be provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the location's operation service center profitability details are returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getLocOperationsServiceCenterProfitabilityBySic(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/service-centers/{sicCd}/operations-profitability'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * ************************************************************** This contract will be deprecated. **
     * <br/>** getLocationReferenceDetails should be used instead. **
     * <br/>************************************************************
     * <br/>Returns location reference information about a service center.
     * @param {?} pathParams
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getLocReferenceDetailsBySic(pathParams, queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/loc-reference-sics/{sicCd}'), {
            pathParams: pathParams,
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation returns the Mexico Transit Time details for the requested origin and destination cities. Rate quotes for Mexico don't have an estimated delivery date, which is why the effective date is not required to perform the operation.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. Valid origin and destination name city names must be provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the details for the Mexico Transit Time record will be returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getMexicoTransitTime(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/mexico-transit-times/from/{originCityName}/to/{destinationCityName}'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation retrieves an offshore port by SIC.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid SIC must be provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the Offshore Port is returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getOffshorePortBySic(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/service-centers/{mainlandSicCd}/offshore-port'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation calculates the number of operational service days between the date the service started at the origin location to the date the service stopped at the destination location.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. A valid origin and destination SIC must be provided.
     * <br/>2. The start date defaults to the current date and the end date to the next day if they aren't provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the service days count is returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} pathParams
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getOperationalServiceDaysCount(pathParams, queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/origins/{originSicCd}/destinations/{destinationSicCd}/service-days-count'), {
            pathParams: pathParams,
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation determines the next business day based on the start date and offset number of days. The number of offset days are added to the start date and the next business day is determined after adjusting for weekends or holidays.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. The input data provided must be valid.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, the next business day is returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} pathParams
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getNextBusinessDay(pathParams, queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/service-centers/{sicCd}/next-business-day'), {
            pathParams: pathParams,
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation determines the next business day based on the country code, business unit, and offset number of days. The number of offset days are added to the start date and the next business day is determined after adjusting for weekends or holidays.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid countryCd must be provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the next business day for the requested country is returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} pathParams
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getNextBusinessDayByCountry(pathParams, queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/countries/{countryCd}/next-business-day'), {
            pathParams: pathParams,
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation retrieves the Loc Address record for the requested SIC.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid reference SIC is provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the corresponding Loc Address record is returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getRefSicAddress(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/service-centers/{refSicCd}/address'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation retrieves hierarchy group names for the requested SICs.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A list of valid SICs is provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then a list of corresponding hierarchy group names is returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getRefSicHierarchyGroupNames(queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/service-centers/hierarchy-group-names'), {
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation returns the Sales Territory and Region SIC for a future customer for the given sales territory code or instance ID.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid salesTerritoryCd or instanceId is provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the Sales Territory and Region SIC details are returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getSalesTerritoryAndRegionSic(queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/sales-territories-and-region-sics'), {
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * Returns the sector number that is associated with the door number provided.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. The SIC and door number are required.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If the input provided is valid, then a sector number will be returned.
     * <br/>2. Otherwise an appropriate error message will be returned.
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getSectorByDoorNumber(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/service-centers/{sicCd}/doors/{doorNumber}'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * Returns location information about a service center. This information shall include the service center's physical address as well as geocoordinates for use in GIS applications.
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getServiceCenterDetailsBySic(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/service-centers/{sicCd}'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation retrieves the SIC associated with the postal codes provided.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. One or more valid US or CA postal codes are provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the SIC, state, country, and other details for the postal codes are returned.
     * <br/>2. Otherwise an appropriate error message will be returned.
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getSicForPostalCodes(queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/postal-assignments'), {
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * Returns the zone and satellite details for a specific SIC location.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. The SIC code is required.  The Boolean fields are optional.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If the data provided is valid, info about the zone and satellite will be returned.
     * <br/>2. Otherwise an appropriate error message will be returned.
     * @param {?} pathParams
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getZoneAndSatelliteBySic(pathParams, queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/zone-satellite/{sicCd}'), {
            pathParams: pathParams,
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This oepration returns a list of all active doors for the service center provided.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. The sicCd is provided and valid.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then a list of all active doors is returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} pathParams
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listAllActiveDoorsBySic(pathParams, queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/service-centers/{sicCd}/all-active-doors'), {
            pathParams: pathParams,
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation returns a list of all active service service centers and their region, operational region, district, city, state, and whether they are a FAC or OB.
     * <br/>
     * <br/>Pre-condition:
     * <br/>None
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then a list of all active SICs will be returned.
     * <br/>2. Otherwise an appropriate error message will be returned.
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listAllActiveSics(dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/all-active-sics'), {}, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * Returns a list of service centers
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listAllSicCdsByRegion(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/sics-by-region/{locGroupCode}'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation lists area assignments based on the input criteria provided.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. One of the following fields must be provided: areaInstId, sicCd. The input criteria can be combined.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, return all area assignments that match the input criteria.
     * <br/>	- If areaInstId is entered, all area assignments pertinent to that areaInstId will be returned.
     * <br/>	- If sicCd is entered, all area assignments pertinent to that sicCd will be returned.
     * <br/>	- If both areaInstId and sicCd are entered, a single area assignment will be returned.
     * <br/>2. If the input data is invalid, then throw an error.
     * <br/>3. If the input data is valid, but no area assignments are found, return a not found message.
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listAreaAssignments(queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/area-assignments'), {
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation returns information about physical location and company operations for one or more SICs.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. At least one valid sicCd is provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the location company operations and reference details are returned for each SIC.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listCompanyOperations(queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/company-operations'), {
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation lists the eligible services for postal code and SIC combination(s).
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. A list of valid postalCd and sicCd combinations must be provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the list of eligible services are returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listEligibleServicesByPostalSic(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/postal-sics/services'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation lists geo area information for location(s).
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. A valid polygonTypeCd must be provided.
     * <br/>2. At least one valid areaInstId or sicCd must be provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then a list of geo areas that matched the input criteria will be returned.
     * <br/>2. Otherwise an appropriate error message will be returned.
     * @param {?} pathParams
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listGeoAreas(pathParams, queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/service-centers/areas/{polygonTypeCd}'), {
            pathParams: pathParams,
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation lists location features based on the input criteria provided.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. One of the following fields must be provided: employeeId, sicCd, or featureCd. The input criteria can be combined.
     * <br/>2. settingValue needs to be provided for a featureCd.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, return all location features that match the input criteria.
     * <br/>	- If sicCd is entered, all features pertinent to that SIC will be returned.
     * <br/>	- If employeeId is entered, all features pertinent to that employeeId will be returned.
     * <br/>	- If featureCd is entered, all features pertinent to that featureCd will be returned.
     * <br/>	- If settingValue is entered, all features pertinent to that settingValue will be returned.
     * <br/>2. If the input data is invalid, then throw an error.
     * <br/>3. If the input data is valid, but no features are found, return a not found message.
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listLocationFeatures(queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/location-features'), {
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * ************************************************************** This contract will be deprecated. **
     * <br/>** listLocationFeatures should be used instead. **
     * <br/>************************************************************
     * <br/>This operation retrieves data for a location feature. The list can be restricted to those of a specific feature value, e.g. 'Y' or 'true' or 'New'. A feature relates to an application. We use the feature codes to identify whether a feature or application has been rolled out to a SIC, handheld device, or employee. For example, if we wanted to track which SICs have been rolled out to use the ELD / Vnomics system, we would create an ELD flag.
     * <br/>
     * <br/>Mandatory Criteria:
     * <br/>- Feature Cd
     * <br/>
     * <br/>Optional Criteria:
     * <br/>- Feature Setting Value
     * @param {?} pathParams
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listLocationFeatureValuesByFeature(pathParams, queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/features/{featureCd}/feature-values'), {
            pathParams: pathParams,
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation returns a list of all location group codes.
     * <br/>​
     * <br/>Pre-conditions:
     * <br/>1. Group category code may be provided (LHO_LHAREA_ALL, LHO_MODEL_GRP) to return all location group codes for the category.
     * <br/>2. A group category code and SIC may be provided to return a specific location group code.
     * <br/>​
     * <br/>Post-conditions:
     * <br/>1. If successful, then a list of all location group codes will be returned.
     * <br/>2. Otherwise an appropriate error message will be returned.
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listLocationGroupCodes(queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/location-group-codes'), {
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * Returns a list of locations and their zones and satellites, including their regions, cities, states, and if they are a FAC or OB. If no input SIC is provided, all locations satisfying the other criteria are returned.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. The activeInd, zoneDesiredInd, and satelliteDesiredInd fields are optional.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If the data provided is valid, then the list of matching locations is returned.
     * <br/>2. Otherwise an appropriate error message will be returned.
     * <br/>
     * <br/>
     * @param {?} request
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listLocations(request, queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/locations'), {
            queryParams: queryParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation lists non-working calendar days based on the filter criteria that are provided.
     * <br/>
     * <br/>Pre-condition:
     * <br/>None
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the non-working calendar days are listed.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listNonWorkDays(queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/non-work-days'), {
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation lists the working or non-working day list on the basis of a given day.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. The input data provided must be valid.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, the operational calendar days are listed.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listOperationalCalendarDays(queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/operational-calendar-days'), {
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation lists the operational hierarchy which is composed of the following: operational regions with all of their SICs and for each SIC all of its zones and satellites.
     * <br/>
     * <br/>Pre-condition:
     * <br/>None
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the operational hierarchy list is returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listOperationalHierarchy(queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/operational-hierarchy'), {
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation lists all operational regions with their districts and for each district all of its SICs.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. Both parameters are empty.
     * <br/>2. A valid locationHierarchyTypeCd is provided.
     * <br/>2.1 If locationHierarchyTypeCd is not empty, a list of valid location codes must be provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If both parameters are empty, then list all operational regions with their districts and for each district all of its SICs.
     * <br/>2. If both parameters are not empty, then list filtered operational regions with their districts and for each district all of its SICs.
     * <br/>3. If only one of the parameters is populated, then an error message identifying the missing parameter will be returned.
     * <br/>4. If there is no such data, then an empty list is returned.
     * <br/>5. Otherwise an appropriate error message is returned.
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listOperationalRegions(queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/operational-regions'), {
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * Lists operational service centers.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>None
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. It should return a list of all existing operational servie centers.
     * <br/>2. Otherwise an appropriate error message will be returned.
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listOperationalServiceCenters(queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/service-centers'), {
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation lists postal codes that are assigned to a SIC.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1.  A valid sicCd is provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then a list of all postal codes for that service center that are effective in the system will be returned.
     * <br/>2. Otherwise an appropriate error message will be returned.
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listPostalAssignmentsForSic(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/service-centers/{sicCd}/postal-assignments'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation either creates a new location feature setting or updates one if it already exists.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. Valid values must be provided for the non-nullable columns of LOC_FEATURE_SETTING.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, a status code of 200 will be returned with the object inserted.
     * <br/>2. Otherwise an appropriate error message will be returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    upsertLocationFeatureSetting(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/location-feature-settings'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This operation either creates new Geo Areas or updates them if they already exist.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. A valid sicCd must be provided.
     * <br/>2. At least one valid LocGeoArea, which contains areaName, description, and shape, must be provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then a status code of 200 will be returned with the object that was upserted.
     * <br/>2. Otherwise an appropriate error message will be returned.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    upsertGeoAreas(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/service-centers/{sicCd}/geo-areas'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * Delete expired messages in the queue
     * @param {?} pathParams
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    deleteExpiredMessages(pathParams, queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/queues/{queueName}/messages'), {
            pathParams: pathParams,
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.delete(apiRequest);
    }
    /**
     * This operation returns counts for the number of ensembles in each of the status cd values.
     * <br/>Pre-conditions:
     * <br/>1. Input search criteria may be provided.
     * <br/>Post-condition:
     * <br/>1. Counts for each ensemble status are returned.
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getEnsembleStatusCounts(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/queues/{queueName}/counts/{fromTmst}/{toTmst}'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation returns counts for PubSub statuses
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getPubSubStatusCounts(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/pubsub/subscriptions/{subscriptionId}/summary/counts/{fromTmst}/{toTmst}'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation returns counts for the number of messages pending in the queue.
     * <br/>Pre-conditions:
     * <br/>1. Queue name is provided.
     * <br/>Post-condition:
     * <br/>1. Count of all messages in ready and wait statuses
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getQueuePendingCount(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/queues/{queueName}/counts/pending'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation return Status of Given Subscription
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getSubscription(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/pubsub/subscriptions/{subscriptionId}'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation returns a count of number of undelivered messages in this PubSub subscription
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getUndeliveredMessages(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/pubsub/subscriptions/{subscriptionId}/undelivered/counts'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation updates one or more SUSPENDED Ensemble execution row to a status of KILLED, in order to indicate it is has been forced to stop processing.
     * <br/>Pre-conditions:
     * <br/>1. One or more Ensemble Instance Ids that is in a status of SUSPENDED is supplied and valid.
     * <br/>Post-condition:
     * <br/>1. Ensemble record(s) updated to a Status of KILLED.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    killEnsemble(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/ensembles/kill'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * List the channels.
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listChannels(dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/channels/'), {}, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * List all expired messages in the queue
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listExpiredMessages(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/queues/{queueName}/messages/{enqueueFromTmst}/{enqueueToTmst}'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * List the queues.
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listQueues(dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/queues'), {}, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * List the Pub/Sub subscriptions.
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listSubscriptions(dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/pubsub/subscriptions/'), {}, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * Replay a message in expired status
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    replayMessage(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/queues/{queueName}/messages/{messageId}'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This operation Restarts a STARTED or NOT_STARTED Ensemble execution based on input Ensemble Instance Id.
     * <br/>Pre-conditions:
     * <br/>1. Input Ensemble Instance Id is supplied and valid.
     * <br/>Post-condition:
     * <br/>1. STARTED or NOT_STARTED Ensemble execution status is updated to STARTED and execution is initiated; if any Minuets exist for the Ensemble, those are initiated in sequence and status updates are performed.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    restartEnsemble(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/queues/{queueName}/ensembles/{ensembleInstId}/restart'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation Resumes a SUSPENDED Ensemble execution based on input Ensemble Instance Id.
     * <br/>Pre-conditions:
     * <br/>1. Input Ensemble Instance Id is supplied and valid.
     * <br/>Post-condition:
     * <br/>1. SUSPENDED Ensemble execution status is updated to STARTED and execution is initiated; if any Minuets exist for the Ensemble, those are initiated in sequence and status updates are performed.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    resumeEnsemble(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/queues/{queueName}/ensembles/{ensembleInstId}/resume'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Start an API Channel.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    startChannel(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/channels/{channelName}/start'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * Start a queue.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    startQueue(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/queues/{queueName}/start'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * Start or restart a PubSub subscription.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    startSubscription(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/pubsub/subscriptions/{subscriptionId}/start'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * Stop an API Channel.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    stopChannel(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/channels/{channelName}/stop'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * Stop a queue. Stopping a queue will only stop the dequeuing process, it will continue to enqueue
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    stopQueue(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/queues/{queueName}/stop'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * Stop a queue. Stopping a queue will only stop the dequeuing process, it will continue to enqueue
     * @param {?} request
     * @param {?} pathParams
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    stopQueueWebhook(request, pathParams, queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/webhooks/queues/{queueName}/stop'), {
            pathParams: pathParams,
            queryParams: queryParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Stop a PubSub subscription.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    stopSubscription(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/pubsub/subscriptions/{subscriptionId}/stop'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * Export documents from elastic index
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. Filter criteria is supplied
     * <br/>
     * <br/>Post-condition:
     * <br/>1. All documents matching the filter will be exported as CSV or Excel format
     * <br/>
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    exportEnsembleStateUnfiltered(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/ensemble-state-unfiltered/exports'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Aggregations from elastic index
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. Filter criteria is supplied
     * <br/>2. One of more fields are supplied
     * <br/>
     * <br/>Post-condition:
     * <br/>1. Aggregations for each supplied field
     * <br/>
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listAggregationsEnsembleStateUnfiltered(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/ensemble-state-unfiltered/aggregations'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Search documents from elastic index
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. Filter criteria is supplied
     * <br/>
     * <br/>Post-condition:
     * <br/>1. Documents matching the filter will be returned
     * <br/>
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    searchEnsembleStateUnfiltered(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/ensemble-state-unfiltered/searches'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Check changed documents against registered filters for ensemble-state-unfiltered
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    changesFeedEnsembleStateUnfiltered(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/ensemble-state-unfiltered/changes'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Register a filter with XRT for EnsembleStateUnfiltered
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    registerFilterEnsembleStateUnfiltered(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/ensemble-state-unfiltered/filters/register'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Unregister a filter with XRT for EnsembleStateUnfiltered
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    unregisterFilterEnsembleStateUnfiltered(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/ensemble-state-unfiltered/filters/unregister'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Delete Elastic document for EnsembleState
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    deleteElasticDocumentEnsembleState(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/elastic/ensemble-state-unfiltered/_doc/{instId}'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.delete(apiRequest);
    }
    /**
     * Get Elastic document from EnsembleState index for given primary key
     * @param {?} pathParams
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getElasticDocumentEnsembleState(pathParams, queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/elastic/ensemble-state-unfiltered/_doc/{instId}'), {
            pathParams: pathParams,
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * Get document count from EnsembleState index
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getElasticDocumentEnsembleStateCount(queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/elastic/ensemble-state-unfiltered/_count'), {
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * Update Elastic document for EnsembleState
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    updateElasticDocumentEnsembleState(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/elastic/ensemble-state-unfiltered/_doc/{instId}/_update'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * List all ensemble configurations
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listEnsembleConfigurations(dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/configurations'), {}, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * List the timers.
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listTimerEnsembles(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/timer-ensembles/{name}/timers'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * Start a timer.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    startTimerEnsemble(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/timers/{name}'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Stop a timer.
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    stopTimerEnsemble(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/timers/{name}'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.delete(apiRequest);
    }
    /**
     * @return {?}
     */
    getEndPoint() {
        return this.configManager.getSetting(LocationApiService.LocationApiEndpoint);
    }
}
LocationApiService.ɵfac = function LocationApiService_Factory(t) { return new (t || LocationApiService)(ɵngcc0.ɵɵinject(ɵngcc1.DataApiService), ɵngcc0.ɵɵinject(ɵngcc2.ConfigManagerService)); };
LocationApiService.ɵprov = /*@__PURE__*/ ɵngcc0.ɵɵdefineInjectable({ token: LocationApiService, factory: LocationApiService.ɵfac });
LocationApiService.LocationApiEndpoint = 'locationV2ApiEndpoint';
/** @nocollapse */
LocationApiService.ctorParameters = () => [
    { type: DataApiService },
    { type: ConfigManagerService }
];
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(LocationApiService, [{
        type: Injectable
    }], function () { return [{ type: ɵngcc1.DataApiService }, { type: ɵngcc2.ConfigManagerService }]; }, null); })();

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes} checked by tsc
 */
const ɵ0 = { configFileUrl: './assets/config.json' };
class SdkLocationModule {
}
SdkLocationModule.ɵfac = function SdkLocationModule_Factory(t) { return new (t || SdkLocationModule)(); };
SdkLocationModule.ɵmod = /*@__PURE__*/ ɵngcc0.ɵɵdefineNgModule({ type: SdkLocationModule });
SdkLocationModule.ɵinj = /*@__PURE__*/ ɵngcc0.ɵɵdefineInjector({ providers: [
        LocationApiService,
        { provide: CONFIG_MANAGER_SERVICE_CONFIGURATION, useValue: ɵ0 },
    ], imports: [[
            ConfigManagerModule
        ]] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(SdkLocationModule, [{
        type: NgModule,
        args: [{
                imports: [
                    ConfigManagerModule
                ],
                providers: [
                    LocationApiService,
                    { provide: CONFIG_MANAGER_SERVICE_CONFIGURATION, useValue: ɵ0 },
                ]
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && ɵngcc0.ɵɵsetNgModuleScope(SdkLocationModule, { imports: function () { return [ConfigManagerModule]; } }); })();

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes} checked by tsc
 */
/**
 *
 */
class CalculateTransitTimeRqst {
    constructor() {
        /**
         * The basic information which is used for calculating transit times.
         * Required: true
         * Array: true
         */
        this.transitTime = undefined;
    }
}
/**
 *
 */
class CalculateTransitTimeResp {
    constructor() {
        /**
         * The transit time details between postal codes.
         * Required: false
         * Array: true
         */
        this.transitTime = undefined;
    }
}
/**
 * Request to delete a location feature.
 */
class DeleteLocationFeatureSettingsQuery {
    constructor() {
        /**
         * The list of unique identifiers which are associated with Location Feature Settings.
         * Required: false
         * Array: true
         */
        this.instIds = undefined;
        /**
         * The unique identifier for a feature.
         * Required: false
         * Array: false
         */
        this.featureCd = undefined;
        /**
         * The unique identifier for a SIC.
         * Required: false
         * Array: false
         */
        this.sicCd = undefined;
    }
}
/**
 * Determines whether the date provided is a non-work date for the location.
 */
class DetermineNonWorkDayPath {
    constructor() {
        /**
         * The service center identification
         * Required: true
         * Array: false
         */
        this.sicCd = undefined;
        /**
         * The date for which this operation will determine whether it is a non-work day for the location.
         * Required: true
         * Array: false
         */
        this.date = undefined;
    }
}
/**
 * Response to the request to determine whether the date provided is a non-work date for the location.
 */
class DetermineNonWorkDayResp {
    constructor() {
        /**
         * The code that determines the type of non-work day.
         * Required: true
         * Array: false
         */
        this.nonWorkDayTypeCd = undefined;
        /**
         * A description of the non-work day.
         * Required: true
         * Array: false
         */
        this.description = undefined;
        /**
         * Indicates whether Linehaul runs on a non-work day.
         * Required: true
         * Array: false
         */
        this.linehaulInd = undefined;
    }
}
/**
 * Path parameters which are used to determine the service date based on offset.
 */
class DetermineOperationalServiceDatePath {
    constructor() {
        /**
         * The service center identification code for the origin location where the service will start.
         * Required: true
         * Array: false
         */
        this.originSicCd = undefined;
        /**
         * The service center identification code for the destination location where the service will end.
         * Required: true
         * Array: false
         */
        this.destinationSicCd = undefined;
    }
}
/**
 * Query parameters which are used to determine the service date based on offset.
 */
class DetermineOperationalServiceDateQuery {
    constructor() {
        /**
         * The calendar date for which determination of service date based on offset will start.
         * Required: false
         * Array: false
         */
        this.calendarDate = undefined;
        /**
         * The code associated with the type of service.
         * Required: false
         * Array: false
         */
        this.serviceTypeCd = undefined;
        /**
         * The number of days to add to the calendar date.
         * Required: false
         * Array: false
         */
        this.offsetDaysCount = undefined;
        /**
         * Indicates the next service date should be returned if the offset of 0 returns a non-service date.
         * Required: false
         * Array: false
         */
        this.findNextServiceDateInd = undefined;
    }
}
/**
 * Response to the request to determine the service date based on offset.
 */
class DetermineOperationalServiceDateResp {
    constructor() {
        /**
         * The service date based on the calendar date and offset provided.
         * Required: false
         * Array: false
         */
        this.serviceDate = undefined;
    }
}
/**
 * Request to return the geo area for a location.
 */
class GetGeoAreasForLocationPath {
    constructor() {
        /**
         * Unique identifier for the service center.
         * Required: true
         * Array: false
         */
        this.sicCd = undefined;
        /**
         * Defines what the polygon coordinates represent.  Possible values are: Area (A), Boundary (B), Fence (F), and Service Center (S).
         * Required: true
         * Array: false
         */
        this.polygonTypeCd = undefined;
    }
}
/**
 * Request to return the geo area for a location.
 */
class GetGeoAreasForLocationQuery {
    constructor() {
        /**
         * The date used to validate active satellite sics.
         * Required: false
         * Array: false
         */
        this.planDate = undefined;
        /**
         * Indicates whether satellite SICs for the parent should be included in the response.
         * Required: false
         * Array: false
         */
        this.satelliteInd = undefined;
        /**
         * Indicates whether geoArea shape data should be included in the response.
         * Required: false
         * Array: false
         */
        this.geoAreaShapeInd = undefined;
    }
}
/**
 * Response to the request to return the geo area for a location.
 */
class GetGeoAreasForLocationResp {
    constructor() {
        /**
         * Details about a geo area.
         * Required: false
         * Array: true
         */
        this.geoArea = undefined;
    }
}
/**
 *
 */
class GetMexicoTransitTimePath {
    constructor() {
        /**
         * The name of the origin city.
         * Required: true
         * Array: false
         */
        this.originCityName = undefined;
        /**
         * The name of the destination city.
         * Required: true
         * Array: false
         */
        this.destinationCityName = undefined;
    }
}
/**
 *
 */
class GetMexicoTransitTimeResp {
    constructor() {
        /**
         * The Mexico Transit Time details which include the number of days needed for the shipment's transit from the origin to the destination.
         * Required: false
         * Array: false
         */
        this.mexicoTransitTime = undefined;
    }
}
/**
 * Request to retrieve the Sales Territory and Region SIC for a future customer.
 */
class GetSalesTerritoryAndRegionSicQuery {
    constructor() {
        /**
         * The sales territory code.
         * Required: false
         * Array: false
         */
        this.salesTerritoryCd = undefined;
        /**
         * The unique identifier for a sales territory.
         * Required: false
         * Array: false
         */
        this.instanceId = undefined;
    }
}
/**
 * Response to the request to retrieve the Sales Territory and Region SIC for a future customer.
 */
class GetSalesTerritoryAndRegionSicResp {
    constructor() {
        /**
         * The Sales Territory details.
         * Required: false
         * Array: false
         */
        this.salesTerritory = undefined;
        /**
         * The Sales Territory Group details.
         * Required: false
         * Array: false
         */
        this.salesTerritoryGroup = undefined;
        /**
         * The Sales Region for a service center.
         * Required: false
         * Array: false
         */
        this.salesRegionSic = undefined;
    }
}
/**
 * Query parameters which are used to retrieve location reference details.
 */
class GetLocationReferenceDetailsQuery {
    constructor() {
        /**
         * The service center identification code for a location.
         * Required: false
         * Array: false
         */
        this.sicCd = undefined;
        /**
         * The unique identifier for a location.
         * Required: false
         * Array: false
         */
        this.instanceId = undefined;
        /**
         * The fields that should be returned as part of the response body.
         * Required: false
         * Array: true
         */
        this.locationDetailCds = undefined;
    }
}
/**
 * Response to the request to retrieve location reference details.
 */
class GetLocationReferenceDetailsResp {
    constructor() {
        /**
         * The location reference details.
         * Required: false
         * Array: false
         */
        this.locationReference = undefined;
        /**
         * The Company Operations details.
         * Required: false
         * Array: false
         */
        this.companyOperations = undefined;
        /**
         * The ConWay Corporation details.
         * Required: false
         * Array: false
         */
        this.conWayCorporation = undefined;
        /**
         * The charge to service center identification code.
         * Required: false
         * Array: false
         */
        this.chargeToSicCd = undefined;
        /**
         * The name of the charge to SIC.
         * Required: false
         * Array: false
         */
        this.chargeToSicName = undefined;
        /**
         * The area service center identification code.
         * Required: false
         * Array: false
         */
        this.areaSicCd = undefined;
        /**
         * The region service center identification code.
         * Required: false
         * Array: false
         */
        this.regionSicCd = undefined;
        /**
         * The satellite service center identification code.
         * Required: false
         * Array: false
         */
        this.satelliteParentSicCd = undefined;
        /**
         * The name of the satellitle parent SIC.
         * Required: false
         * Array: false
         */
        this.satelliteParentSicName = undefined;
        /**
         * >The zone parent service center identification code.
         * Required: false
         * Array: false
         */
        this.zoneParentSicCd = undefined;
        /**
         * The name of the zone parent SIC.
         * Required: false
         * Array: false
         */
        this.zoneParentSicName = undefined;
        /**
         * The linehaul service center identification code.
         * Required: false
         * Array: false
         */
        this.linehaulHostSicCd = undefined;
        /**
         * The name of the linehaul host SIC.
         * Required: false
         * Array: false
         */
        this.linehaulHostSicName = undefined;
        /**
         * The location Hierarchy Group details for the region.
         * Required: false
         * Array: false
         */
        this.regionLocationHierarchyGroup = undefined;
        /**
         * The location Hierarchy Group details for the area.
         * Required: false
         * Array: false
         */
        this.areaLocationHierarchyGroup = undefined;
    }
}
/**
 * Query parameters which are used to retrieve location hierarchy group details.
 */
class GetLocHierarchyGroupQuery {
    constructor() {
        /**
         * The service center identification code.
         * Required: true
         * Array: false
         */
        this.sicCd = undefined;
        /**
         * The hierarchy category code of the group.
         * Required: true
         * Array: true
         */
        this.hierarchyGroupCategoryCds = undefined;
    }
}
/**
 * Response to the request to retrieve location hierarchy group details.
 */
class GetLocHierarchyGroupResp {
    constructor() {
        /**
         * The Location Reference details.
         * Required: false
         * Array: false
         */
        this.locationReference = undefined;
        /**
         * The location Hierarchy Group details for the SIC.
         * Required: false
         * Array: false
         */
        this.locationHierarchyGroup = undefined;
    }
}
/**
 * Path parameter used to return operation service center profitability details for the sicCd provided.
 */
class GetLocOperationsServiceCenterProfitabilityBySicPath {
    constructor() {
        /**
         * The unique identifier for the service center.
         * Required: true
         * Array: false
         */
        this.sicCd = undefined;
    }
}
/**
 * Response to the request to return operation service center profitability details for the sicCd provided.
 */
class GetLocOperationsServiceCenterProfitabilityBySicResp {
    constructor() {
        /**
         * The operation service center profitability details for a location.
         * Required: false
         * Array: false
         */
        this.locOperationsServiceCentersProfitability = undefined;
    }
}
/**
 * Path parameter which is used to retrieve an Offshore Port.
 */
class GetOffshorePortBySicPath {
    constructor() {
        /**
         * The service center identification code for the Offshore Port.
         * Required: true
         * Array: false
         */
        this.mainlandSicCd = undefined;
    }
}
/**
 * Response to the request to retrieve an Offshore Port.
 */
class GetOffshorePortBySicResp {
    constructor() {
        /**
         * The Offshore Port for the requested SIC.
         * Required: false
         * Array: false
         */
        this.offshorePort = undefined;
    }
}
/**
 * Path parameters which are used to calculate the service days count.
 */
class GetOperationalServiceDaysCountPath {
    constructor() {
        /**
         * The service center identification code for the origin location where the service started.
         * Required: true
         * Array: false
         */
        this.originSicCd = undefined;
        /**
         * The service center identification code for the destination location where the service ended.
         * Required: true
         * Array: false
         */
        this.destinationSicCd = undefined;
    }
}
/**
 * Query parameters which are used to calculate the service days count.
 */
class GetOperationalServiceDaysCountQuery {
    constructor() {
        /**
         * The date for which the service started.
         * Required: false
         * Array: false
         */
        this.startDate = undefined;
        /**
         * The date for which the service ended.
         * Required: false
         * Array: false
         */
        this.endDate = undefined;
        /**
         * The code associated with the type of service.
         * Required: false
         * Array: false
         */
        this.serviceTypeCd = undefined;
    }
}
/**
 * Response to the request to calculate the service days.
 */
class GetOperationalServiceDaysCountResp {
    constructor() {
        /**
         * The number of days it took to complete the service.
         * Required: false
         * Array: false
         */
        this.serviceDaysCount = undefined;
    }
}
/**
 * Path parameter which is used to retrieve the Loc Address record for a SIC.
 */
class GetRefSicAddressPath {
    constructor() {
        /**
         * The reference service center identification code.
         * Required: true
         * Array: false
         */
        this.refSicCd = undefined;
    }
}
/**
 * Response to the request to retrieve the Loc Address record for a SIC.
 */
class GetRefSicAddressResp {
    constructor() {
        /**
         * The Loc Address record which corresponds to the reference SIC.
         * Required: false
         * Array: false
         */
        this.locAddress = undefined;
    }
}
/**
 * Query parameter which is used to retrieve hierarchy group names for SICs.
 */
class GetRefSicHierarchyGroupNamesQuery {
    constructor() {
        /**
         * The list of reference service center identification codes.
         * Required: true
         * Array: true
         */
        this.refSicCds = undefined;
    }
}
/**
 * Response to the request to retrieve hierarchy group names for SICs.
 */
class GetRefSicHierarchyGroupNamesResp {
    constructor() {
        /**
         * The hierarchy group names corresponding to the reference SICs.
         * Required: false
         * Array: true
         */
        this.hierarchyGroupNames = undefined;
    }
}
/**
 * Request to return the sector by the door number provided.
 */
class GetSectorByDoorNumberPath {
    constructor() {
        /**
         * The location for which the sector is to be retrieved.
         * Required: true
         * Array: false
         */
        this.sicCd = undefined;
        /**
         * The door number for which the sector is to be retrieved.
         * Required: true
         * Array: false
         */
        this.doorNumber = undefined;
    }
}
/**
 * Response to the request to return the sector by the door number provided.
 */
class GetSectorByDoorNumberResp {
    constructor() {
        /**
         * The sector number associated with the door number.
         * Required: false
         * Array: false
         */
        this.sectorNumber = undefined;
        /**
         * The location for which the sector is to be retrieved.
         * Required: false
         * Array: false
         */
        this.sicCd = undefined;
        /**
         * The door number for which the sector is to be retrieved.
         * Required: false
         * Array: false
         */
        this.doorNumber = undefined;
    }
}
/**
 * Query parameters used to retrieve the SIC assigned to the postal codes provided.
 */
class GetSicForPostalCodesQuery {
    constructor() {
        /**
         * The list of US or CA postal codes for which the SIC will be retrieved.
         * Required: true
         * Array: true
         */
        this.postalCds = undefined;
    }
}
/**
 * Response to the request to retrieve the SIC assigned to the postal codes provided.
 */
class GetSicForPostalCodesResp {
    constructor() {
        /**
         * The list of SICs that are assigned to the postal codes provided along with additional details.
         * Required: false
         * Array: true
         */
        this.postalSicAssignments = undefined;
        /**
         * The validation error details for the list of invalid postal codes.
         * Required: false
         * Array: true
         */
        this.validationErrors = undefined;
    }
}
/**
 * Request to retrieve all active doors by SIC.
 */
class ListAllActiveDoorsBySicPath {
    constructor() {
        /**
         * The code associated with the service center for which all active doors will be retrieved.
         * Required: true
         * Array: false
         */
        this.sicCd = undefined;
    }
}
/**
 * Query parameters used to return the doors for a location.
 */
class ListAllActiveDoorsBySicQuery {
    constructor() {
        /**
         * The area inst id for which the door is to be retrieved.
         * Required: true
         * Array: false
         */
        this.areaInstId = undefined;
    }
}
/**
 * Response to the request to list all active doors by a service center.
 */
class ListAllActiveDoorsBySicResp {
    constructor() {
        /**
         * The list of active doors that are associated with the SIC provided.
         * Required: false
         * Array: true
         */
        this.activeDoors = undefined;
    }
}
/**
 * Request to list all active SICs.
 */
class ListAllActiveSicsRqst {
}
/**
 * Response to the request to list all active service service centers and their region, operational region, district, city, state, and if they are a FAC or OB.
 */
class ListAllActiveSicsResp {
    constructor() {
        /**
         * A list of active service centers and their details.
         * Required: false
         * Array: true
         */
        this.activeSics = undefined;
    }
}
/**
 * Request to retrieve all sic codes by region.
 */
class ListAllSicCdsByRegionPath {
    constructor() {
        /**
         * The code identifying the region.
         * Required: true
         * Array: false
         */
        this.locGroupCode = undefined;
    }
}
/**
 * Response to the request to list sic codes by region code.
 */
class ListAllSicCdsByRegionResp {
    constructor() {
        /**
         * A list of the sic codes.
         * Required: false
         * Array: true
         */
        this.sicCds = undefined;
    }
}
/**
 * Query parameters used to filter the list of area assignments returned.
 */
class ListAreaAssignmentsQuery {
    constructor() {
        /**
         * The unique identifier for the area assignment is to be retrieved.
         * Required: false
         * Array: false
         */
        this.areaInstId = undefined;
        /**
         * The service center identification code for the location.
         * Required: false
         * Array: false
         */
        this.sicCd = undefined;
    }
}
/**
 * Response to the request to list area assignments that match the input criteria.
 */
class ListAreaAssignmentsResp {
    constructor() {
        /**
         * The list of area assignments that match the input criteria.
         * Required: false
         * Array: true
         */
        this.areaAssignments = undefined;
    }
}
/**
 * Query parameter which is used to list location references.
 */
class ListCompanyOperationsQuery {
    constructor() {
        /**
         * The service center identification codes for the locations.
         * Required: true
         * Array: true
         */
        this.sicCds = undefined;
    }
}
/**
 * Response to the request to list location references.
 */
class ListCompanyOperationsResp {
    constructor() {
        /**
         * The list of information about the physical location and business operations for each SIC.
         * Required: false
         * Array: true
         */
        this.locationCompanyOperations = undefined;
    }
}
/**
 * Request to list eligible services by postal SIC.
 */
class ListEligibleServicesByPostalSicRqst {
    constructor() {
        /**
         * The list of postal SIC codes for which the eligible services will be retrieved.
         * Required: false
         * Array: true
         */
        this.postalSics = undefined;
    }
}
/**
 * Response to the request to list eligible services by postal SIC.
 */
class ListEligibleServicesByPostalSicResp {
    constructor() {
        /**
         * The list of postal SIC eligible services that match the filter criteria.
         * Required: false
         * Array: true
         */
        this.postalSicServices = undefined;
    }
}
/**
 * Path parameter used to list geo areas for locations.
 */
class ListGeoAreasPath {
    constructor() {
        /**
         * Defines what the polygon coordinates represent.  Possible values are: Area (A), Boundary (B), Fence (F), and Service Center (S).
         * Required: true
         * Array: false
         */
        this.polygonTypeCd = undefined;
    }
}
/**
 * Query parameters used to list geo areas for locations.
 */
class ListGeoAreasQuery {
    constructor() {
        /**
         * The list of geo area identifiers.
         * Required: false
         * Array: true
         */
        this.areaInstIds = undefined;
        /**
         * The list of service center identification codes.
         * Required: false
         * Array: true
         */
        this.sicCds = undefined;
        /**
         * The date used to validate active satellite SICs.
         * Required: false
         * Array: false
         */
        this.planDate = undefined;
        /**
         * Indicates whether satellite SICs for the parent should be included in the response.
         * Required: false
         * Array: false
         */
        this.satelliteInd = undefined;
        /**
         * Indicates whether geo area shape data should be included in the response.
         * Required: false
         * Array: false
         */
        this.geoAreaShapeInd = undefined;
    }
}
/**
 * Response to the request to return the geo area for a location.
 */
class ListGeoAreasResp {
    constructor() {
        /**
         * The list of geo areas associated with the locations provided.
         * Required: false
         * Array: true
         */
        this.geoAreas = undefined;
    }
}
/**
 * Query parameters used to filter the list location features returned.
 */
class ListLocationFeaturesQuery {
    constructor() {
        /**
         * The unique identifier for the employee.
         * Required: false
         * Array: false
         */
        this.employeeId = undefined;
        /**
         * The unique identifier for the SIC.
         * Required: false
         * Array: false
         */
        this.sicCd = undefined;
        /**
         * The code that represents the feature for which details are to be retrieved.
         * Required: false
         * Array: false
         */
        this.featureCd = undefined;
        /**
         * The value attached to the specific feature. For example - 'Y'.
         * Required: false
         * Array: false
         */
        this.settingValue = undefined;
        /**
         * Pagination information to retrieve the list.
         * Required: false
         * Array: false
         */
        this.listInfo = undefined;
    }
}
/**
 * Response to the request to list location features that match the input criteria.
 */
class ListLocationFeaturesResp {
    constructor() {
        /**
         * The list of location features that matched the input criteria.
         * Required: false
         * Array: true
         */
        this.locationFeatures = undefined;
        /**
         * Pagination information to retrieve the list.
         * Required: false
         * Array: false
         */
        this.listInfo = undefined;
    }
}
/**
 * Query to retrieve a list location group codes.
 */
class ListLocationGroupCodesQuery {
    constructor() {
        /**
         * The category code groups the SICs by linehaul region.
         * Required: false
         * Array: false
         */
        this.groupCategoryCd = undefined;
        /**
         * Used to filter the list by a specific location.
         * Required: false
         * Array: false
         */
        this.sicCd = undefined;
    }
}
/**
 * Response to the request to list operational service centers.
 */
class ListLocationGroupCodesResp {
    constructor() {
        /**
         * A list of the regions along with the SICs that are part of that region.
         * Required: false
         * Array: true
         */
        this.locationGroupCodes = undefined;
    }
}
/**
 * Query to list locations.
 */
class ListLocationsQuery {
    constructor() {
        /**
         * Indicates if only active locations should be included.
         * Required: false
         * Array: false
         */
        this.activeInd = undefined;
        /**
         * Indicates if zone locations should be included.
         * Required: false
         * Array: false
         */
        this.zoneDesiredInd = undefined;
        /**
         * Indicates if zone locations must have a physical address.
         * Required: false
         * Array: false
         */
        this.zoneWithPhysicalAddressInd = undefined;
        /**
         * Indicates if satellite locations should be included.
         * Required: false
         * Array: false
         */
        this.satelliteDesiredInd = undefined;
        /**
         * Indicates if meet and turn SICs should be returned.
         * Required: false
         * Array: false
         */
        this.meetAndTurnDesiredInd = undefined;
    }
}
/**
 * Request to list locations
 */
class ListLocationsRqst {
    constructor() {
        /**
         * List of SIC codes desired. If not supplied, list all locations based on the other criteria.
         * Required: false
         * Array: true
         */
        this.refSicCd = undefined;
        /**
         * A list of group categories to include.
         * Required: false
         * Array: true
         */
        this.groupCategoryCd = undefined;
    }
}
/**
 * Response to list locations and their zones and satellites, including its region, city, state, and if they are a FAC or OB.
 */
class ListLocationsResp {
    constructor() {
        /**
         * A list of locations that match the input criteria.
         * Required: false
         * Array: true
         */
        this.locationSic = undefined;
    }
}
/**
 * Query parameters which are used to list non-work calendar days.
 */
class ListNonWorkDaysQuery {
    constructor() {
        /**
         * The beginning of the date range which is used to filter the results.
         * Required: false
         * Array: false
         */
        this.startDate = undefined;
        /**
         * The end of the date range which is used to filter the results.
         * Required: false
         * Array: false
         */
        this.endDate = undefined;
        /**
         * The two character code associated with the country.
         * Required: false
         * Array: false
         */
        this.countryCd = undefined;
        /**
         * The number that is used by the Sales team (including BI) to identify how a particular day is weighted as far as revenue is concerned.  For Revenue use 1.0 and for Weekend and Holiday use 0.
         * Required: false
         * Array: false
         */
        this.revenueAllocationNbr = undefined;
        /**
         * Pagination information to retrieve the list.
         * Required: false
         * Array: false
         */
        this.listInfo = undefined;
    }
}
/**
 * Response to the request to list the non-work calendar days that match the filter criteria.
 */
class ListNonWorkDaysResp {
    constructor() {
        /**
         * The list of operational non-work days which matched the filter criteria.
         * Required: false
         * Array: true
         */
        this.nonWorkCalendarDays = undefined;
        /**
         * Pagination information to retrieve the list.
         * Required: false
         * Array: false
         */
        this.listInfo = undefined;
    }
}
/**
 * Query parameter which is used to list the operational hierarchy.
 */
class ListOperationalHierarchyQuery {
    constructor() {
        /**
         * Pagination information to retrieve the list.
         * Required: false
         * Array: false
         */
        this.listInfo = undefined;
    }
}
/**
 * Response to the request to list the operational hierarchy.
 */
class ListOperationalHierarchyResp {
    constructor() {
        /**
         * The list of all operational regions, host SICs, zones, and satellites.
         * Required: false
         * Array: true
         */
        this.operationalHierarchy = undefined;
    }
}
/**
 * Request to list all operational regions and all of their SICs.
 */
class ListOperationalRegionsQuery {
    constructor() {
        /**
         * The type of location which will be used to filter the data. Permitted values: REGION and SIC.
         * Required: false
         * Array: false
         */
        this.locationHierarchyTypeCd = undefined;
        /**
         * The value used to filter the results based on the selected locationHierarchyTypeCd. The values should be the codes of the registry in the Database. Example:
         * 						LocationHierarchyTypeCd = 'REGION' and locationHierarchyFilters = ['CEA', 'CCA'], to filter by REGIONs Eastern and Mid-West
         * 						LocationHierarchyTypeCd = 'SIC' and locationHierarchyFilters = ['NAA', 'NAB'], to filter by the SICs 'NAA' and 'NAB'
         * Required: false
         * Array: true
         */
        this.locationHierarchyFilters = undefined;
    }
}
/**
 * Response wiht the of list all Operational Regions with their Districts, and for each District, all SICs..
 */
class ListOperationalRegionsResp {
    constructor() {
        /**
         * A list of all operational regions which match the filter criteria.
         * Required: false
         * Array: true
         */
        this.operationalRegions = undefined;
    }
}
/**
 * Request to list operational service centers.
 */
class ListOperationalServiceCentersQuery {
    constructor() {
        /**
         * Indicates whether satellite service centers should be excluded from the response.
         * Required: false
         * Array: false
         */
        this.excludeSatellitesInd = undefined;
        /**
         * Pagination information to retrieve the list.
         * Required: false
         * Array: false
         */
        this.listInfo = undefined;
    }
}
/**
 * Response to the request to list operational service centers.
 */
class ListOperationalServiceCentersResp {
    constructor() {
        /**
         * A list of operational service centers.
         * Required: false
         * Array: true
         */
        this.serviceCenters = undefined;
    }
}
/**
 * The path parameter which is used to list postal assignments for a SIC.
 */
class ListPostalAssignmentsForSicPath {
    constructor() {
        /**
         * The service center identification code.
         * Required: true
         * Array: false
         */
        this.sicCd = undefined;
    }
}
/**
 * Response to the request to list postal assignments for a SIC.
 */
class ListPostalAssignmentsForSicResp {
    constructor() {
        /**
         * The list of effective postal codes for the specified service center.
         * Required: false
         * Array: true
         */
        this.postalAssignments = undefined;
    }
}
/**
 * Request to insert or update a location feature setting.
 */
class UpsertLocationFeatureSettingRqst {
    constructor() {
        /**
         * The feature setting which will be created or update the existing entry.
         * Required: true
         * Array: false
         */
        this.featureSetting = undefined;
    }
}
/**
 * Response to the request to insert or update a location feature setting.
 */
class UpsertLocationFeatureSettingResp {
    constructor() {
        /**
         * The feature setting which was created or updated the existing entry.
         * Required: false
         * Array: false
         */
        this.featureSetting = undefined;
    }
}
/**
 * Request to return the host SIC for a zone.
 */
class GetHostSicDetailsPath {
    constructor() {
        /**
         * Unique identifier for the zone service center.
         * Required: true
         * Array: false
         */
        this.sicCd = undefined;
    }
}
/**
 * Response to the request to return the host SIC for a zone.
 */
class GetHostSicDetailsResp {
    constructor() {
        /**
         * Unique identifier for the zone service center.
         * Required: true
         * Array: false
         */
        this.sicCd = undefined;
        /**
         * Unique identifier for the host service center.
         * Required: false
         * Array: false
         */
        this.hostSicCd = undefined;
        /**
         * Identifies if the SIC is zone or satellite.
         * Required: false
         * Array: false
         */
        this.sicType = undefined;
    }
}
/**
 * Request to retrieve location reference details for a service center location.
 */
class GetLocReferenceDetailsBySicPath {
    constructor() {
        /**
         * The code identifying a given service center
         * Required: false
         * Array: false
         */
        this.sicCd = undefined;
    }
}
/**
 * The query parameter for the request to get the loc reference for a SIC.
 */
class GetLocReferenceDetailsBySicQuery {
    constructor() {
        /**
         * The fields that should be returned as part of the response body.
         * Required: false
         * Array: true
         */
        this.responseFields = undefined;
    }
}
/**
 * Response that contains location reference detail information for a service center location.
 */
class GetLocReferenceDetailsBySicResp {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.locReference = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.locCompanyOperations = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.locConWayCorporation = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.chargeToSicCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.chargeToSicNm = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.areaSicCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.regionSicCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.satelliteParentSicCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.satelliteParentSicNm = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.zoneParentSicCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.zoneParentSicNm = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.linehaulHostSicCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.linehaulHostSicNm = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.regionLocHierarchyGroup = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.areaLocHierarchyGroup = undefined;
    }
}
/**
 * Path parameter used to determine the next business day.
 */
class GetNextBusinessDayPath {
    constructor() {
        /**
         * The service center identification code.
         * Required: true
         * Array: false
         */
        this.sicCd = undefined;
    }
}
/**
 * Query parameters used to determine the next business day.
 */
class GetNextBusinessDayQuery {
    constructor() {
        /**
         * The date for which the next business day will be determined.
         * Required: false
         * Array: false
         */
        this.startDate = undefined;
        /**
         * The number of days to add to to the start date.
         * Required: false
         * Array: false
         */
        this.offsetDaysCount = undefined;
        /**
         * Identifies the operation to be executed. If no value is passed, NextBusinessDay is used as the default.
         * Required: false
         * Array: false
         */
        this.nextBusinessDayOperationCd = undefined;
    }
}
/**
 * Response to the request to determine the next business day.
 */
class GetNextBusinessDayResp {
    constructor() {
        /**
         * The next business day after adding offset days to the start date.
         * Required: false
         * Array: false
         */
        this.nextBusinessDay = undefined;
        /**
         * The date for which the next business day was determined.
         * Required: false
         * Array: false
         */
        this.startDate = undefined;
        /**
         * The number of days to add to to the start date.
         * Required: false
         * Array: false
         */
        this.offsetDaysCount = undefined;
        /**
         * Identifies the type of non-work day.
         * Required: false
         * Array: false
         */
        this.nonWorkDayTypeCd = undefined;
        /**
         * Indicates whether the given date is marked for linehaul. Possible values: Y or N.
         * Required: false
         * Array: false
         */
        this.linehaulInd = undefined;
        /**
         * Indicates whether the given date is marked for non-work. Possible values: Y or N.
         * Required: false
         * Array: false
         */
        this.nonWorkDayInd = undefined;
        /**
         * The revenue allocation for the given date.  This is use by the Sales team (including BI) to identify how a particular day is weighted as far as revenue is concerned.
         * Required: false
         * Array: false
         */
        this.revenueAllocationNbr = undefined;
        /**
         * The operation that was executed for the next business day calculation.
         * Required: false
         * Array: false
         */
        this.nextBusinessDayOperationCd = undefined;
    }
}
/**
 * Path parameter which is used to determine the next business day.
 */
class GetNextBusinessDayByCountryPath {
    constructor() {
        /**
         * The two character code associated with the country.
         * Required: true
         * Array: false
         */
        this.countryCd = undefined;
    }
}
/**
 * Query parameters which are used to determine the next business day by country.
 */
class GetNextBusinessDayByCountryQuery {
    constructor() {
        /**
         * The date for which the next business day will be determined.
         * Required: false
         * Array: false
         */
        this.startDate = undefined;
        /**
         * The number of days which will be added to the start date.
         * Required: false
         * Array: false
         */
        this.offsetDaysCount = undefined;
        /**
         * The US state or CA province code.
         * Required: false
         * Array: false
         */
        this.stateCd = undefined;
    }
}
/**
 * Response to the request to determine the next business day by country.
 */
class GetNextBusinessDayByCountryResp {
    constructor() {
        /**
         * The next business day which resulted after adding the offset days to the start date.
         * Required: false
         * Array: false
         */
        this.nextBusinessDay = undefined;
        /**
         * The date for which the next business day was determined.
         * Required: false
         * Array: false
         */
        this.startDate = undefined;
        /**
         * The number of days that were added to to the start date.
         * Required: false
         * Array: false
         */
        this.offsetDaysCount = undefined;
        /**
         * The code associated with the type of non-work day.
         * Required: false
         * Array: false
         */
        this.nonWorkDayTypeCd = undefined;
        /**
         * Indicates whether the given date is marked for linehaul. Permitted values: Y and N.
         * Required: false
         * Array: false
         */
        this.linehaulInd = undefined;
        /**
         * Indicates whether the given date is marked for non-work. Permitted values: Y and N.
         * Required: false
         * Array: false
         */
        this.nonWorkDayInd = undefined;
        /**
         * The revenue allocation for the given date.  This is used by the Sales team (including BI) to identify how a particular day is weighted as far as revenue is concerned.
         * Required: false
         * Array: false
         */
        this.revenueAllocationNbr = undefined;
        /**
         * The code associated with the operation that was executed for the next business day calculation.
         * Required: false
         * Array: false
         */
        this.nextBusinessDayOperationCd = undefined;
    }
}
/**
 * Request to retrieve detailed information for a service center location.
 */
class GetServiceCenterDetailsBySicPath {
    constructor() {
        /**
         * The code identifying a given service center.
         * Required: true
         * Array: false
         */
        this.sicCd = undefined;
    }
}
/**
 * Response that contains detailed information for a service center location.
 */
class GetServiceCenterDetailsBySicResp {
    constructor() {
        /**
         * The code identifying a given service center
         * Required: false
         * Array: false
         */
        this.sicCd = undefined;
        /**
         * The code identifying the component for the sic
         * Required: false
         * Array: false
         */
        this.componentCd = undefined;
        /**
         * address of service center
         * Required: false
         * Array: false
         */
        this.address = undefined;
        /**
         * GIS lat long
         * Required: false
         * Array: false
         */
        this.coordinates = undefined;
        /**
         * The offset of the timezone.
         * Required: false
         * Array: false
         */
        this.timezoneOffset = undefined;
    }
}
/**
 * Path to return zone and satellite details for a specific SIC location.
 */
class GetZoneAndSatelliteBySicPath {
    constructor() {
        /**
         * The SIC code for a service center.
         * Required: true
         * Array: false
         */
        this.sicCd = undefined;
    }
}
/**
 * Query to return zone and satellite details for a specific SIC location.
 */
class GetZoneAndSatelliteBySicQuery {
    constructor() {
        /**
         * The true/false indicator denoting if the zone should be returned.
         * Required: false
         * Array: false
         */
        this.zoneInd = undefined;
        /**
         * The true/false indicator denoting if the satellite should be returned.
         * Required: false
         * Array: false
         */
        this.satelliteInd = undefined;
    }
}
/**
 * Response to the request to return zone and satellite details for a specific SIC location.
 */
class GetZoneAndSatelliteBySicResp {
    constructor() {
        /**
         * The SIC used in the search (same as the input SIC).
         * Required: false
         * Array: false
         */
        this.refSic = undefined;
        /**
         * The region for the import SIC (host region for the sic).
         * Required: false
         * Array: false
         */
        this.refRegionSic = undefined;
        /**
         * The host of the import SIC if the import SIC was a zone.
         * Required: false
         * Array: false
         */
        this.refZoneSic = undefined;
        /**
         * The host of the import SIC if the import SIC was a satellite.
         * Required: false
         * Array: false
         */
        this.refSatelliteSic = undefined;
        /**
         * The SIC code for a service center.
         * Required: false
         * Array: true
         */
        this.zoneSatelliteInfo = undefined;
    }
}
/**
 * Path parameter used to return a list of values for a location feature.
 */
class ListLocationFeatureValuesByFeaturePath {
    constructor() {
        /**
         * The code that represents the feature for which details are to be retrieved.
         * Required: true
         * Array: false
         */
        this.featureCd = undefined;
    }
}
/**
 * Query parameter used to return a list of values for a location feature.
 */
class ListLocationFeatureValuesByFeatureQuery {
    constructor() {
        /**
         * The value attached to the specific feature. For example - 'Y'.
         * Required: false
         * Array: false
         */
        this.settingValue = undefined;
    }
}
/**
 * Response to the request to return a list of values for a location feature.
 */
class ListLocationFeatureValuesByFeatureResp {
    constructor() {
        /**
         * A list of location features associated with the feature code and optionally the feature value.
         * Required: false
         * Array: true
         */
        this.locationFeatures = undefined;
    }
}
/**
 * Query parameters used to list operational calendar days.
 */
class ListOperationalCalendarDaysQuery {
    constructor() {
        /**
         * The beginning date to check for a work day or non-working day.
         * Required: false
         * Array: false
         */
        this.startDate = undefined;
        /**
         * The end date to check for a work day or non-working day.
         * Required: false
         * Array: false
         */
        this.endDate = undefined;
        /**
         * The code associated with the type of revenue day.  Valid values: R (Revenue day), W (Weekend day), and H (Holiday).
         * Required: false
         * Array: false
         */
        this.revenueDayCd = undefined;
        /**
         * For Revenue use 1.0 and 0 for Weekend and Holiday.
         * Required: false
         * Array: false
         */
        this.revenueAllocationNbrStr = undefined;
        /**
         * Pagination information to retrieve the list.
         * Required: false
         * Array: false
         */
        this.listInfo = undefined;
    }
}
/**
 * Response to the request to list the operational calendard days that match the filter criteria.
 */
class ListOperationalCalendarDaysResp {
    constructor() {
        /**
         * The list of operation caldendar days which match the filter criteria.
         * Required: false
         * Array: true
         */
        this.operationalCalendarDays = undefined;
        /**
         * Pagination information to retrieve the list.
         * Required: false
         * Array: false
         */
        this.listInfo = undefined;
    }
}
/**
 * Path parameter which is used to upsert Geo Areas of a SIC.
 */
class UpsertGeoAreasPath {
    constructor() {
        /**
         * The service center identification code.
         * Required: true
         * Array: false
         */
        this.sicCd = undefined;
    }
}
/**
 * Request to upsert Geo Areas of a SIC.
 */
class UpsertGeoAreasRqst {
    constructor() {
        /**
         * The details of the Geo Areas that will be upserted.
         * Required: true
         * Array: true
         */
        this.geoAreas = undefined;
    }
}
/**
 * Response to the request to upsert Geo Areas of a SIC.
 */
class UpsertGeoAreasResp {
    constructor() {
        /**
         * The details of the Geo Areas that were upserted.
         * Required: false
         * Array: true
         */
        this.geoAreas = undefined;
    }
}
/**
 * Details about an active service center.
 */
class ActiveSic {
    constructor() {
        /**
         * The service center identification code.
         * Required: true
         * Array: false
         */
        this.sicCd = undefined;
        /**
         * The code associated with the group that belongs to the service center.
         * Required: false
         * Array: false
         */
        this.groupCd = undefined;
        /**
         * The region that belongs to the service center.
         * Required: false
         * Array: false
         */
        this.region = undefined;
        /**
         * The operational region of the service center.
         * Required: false
         * Array: false
         */
        this.operationalRegion = undefined;
        /**
         * The district for the service center.
         * Required: false
         * Array: false
         */
        this.district = undefined;
        /**
         * The city for service center.
         * Required: false
         * Array: false
         */
        this.city = undefined;
        /**
         * The offset of the service center.
         * Required: false
         * Array: false
         */
        this.serviceCenterOffset = undefined;
        /**
         * The state for the service center.
         * Required: false
         * Array: false
         */
        this.state = undefined;
        /**
         * Returns 1 if SIC is a FAC.
         * Required: false
         * Array: false
         */
        this.isFac = undefined;
        /**
         * Returns 1 if SIC is a OB.
         * Required: false
         * Array: false
         */
        this.isOb = undefined;
        /**
         * A description for the location.
         * Required: false
         * Array: false
         */
        this.sicDescription = undefined;
    }
}
/**
 * District represents a specific geographical location under a Region which is composed of a group of SIC codes. Examples: Chicago, Detroit, Milwaukee, etc.
 */
class District {
    constructor() {
        /**
         * The code used as an identifier on the database.
         * Required: false
         * Array: false
         */
        this.districtCd = undefined;
        /**
         * The name of the district. E.g. Chicago, Detroit, Milwaukee, etc.
         * Required: false
         * Array: false
         */
        this.districtName = undefined;
        /**
         * A list of the service center identification codes that belong to that district.
         * Required: false
         * Array: true
         */
        this.sicCds = undefined;
    }
}
/**
 * Details about a geo area.
 */
class GeoArea {
    constructor() {
        /**
         * The unique identifier for the geo area.
         * Required: false
         * Array: false
         */
        this.areaInstId = undefined;
        /**
         * The service center identification code.
         * Required: false
         * Array: false
         */
        this.sicCd = undefined;
        /**
         * The name for the geo area.
         * Required: false
         * Array: false
         */
        this.geoAreaName = undefined;
        /**
         * The description for the geo area.
         * Required: false
         * Array: false
         */
        this.geoAreaDesc = undefined;
        /**
         * Defines what the polygon coordinates represent.  Possible values are: Area (A), Boundary (B), Fence (F), and Service Center (S).
         * Required: false
         * Array: false
         */
        this.polygonTypeCd = undefined;
        /**
         * The coordinates for the locations.
         * Required: false
         * Array: true
         */
        this.polygon = undefined;
        /**
         * Standard audit trail information.
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 * The host SIC, which has zones/satellites linked to it in the hierarchy. E.g. UPO-PORTLAND.
 */
class HostSic {
    constructor() {
        /**
         * The service center identification code.
         * Required: false
         * Array: false
         */
        this.sicCd = undefined;
        /**
         * The category (host, zone, or satellite) of the service center in the operational hierarchy group.
         * Required: false
         * Array: false
         */
        this.sicCategory = undefined;
        /**
         * Address infomration for the host service center.
         * Required: false
         * Array: false
         */
        this.locAddress = undefined;
        /**
         * Reference details about a host service center.
         * Required: false
         * Array: false
         */
        this.reference = undefined;
        /**
         * The list of zone service centers linked to the host SIC.
         * Required: false
         * Array: true
         */
        this.zoneSics = undefined;
        /**
         * The list of satellite service centers linked to the host SIC.
         * Required: false
         * Array: true
         */
        this.satelliteSics = undefined;
    }
}
/**
 * The details about a Location Reference and Company Operations.
 */
class LocationCompanyOperation {
    constructor() {
        /**
         * The information about the physical location of a service center.
         * Required: false
         * Array: false
         */
        this.locationReference = undefined;
        /**
         * The information about the business operations of a location.
         * Required: false
         * Array: false
         */
        this.companyOperations = undefined;
    }
}
/**
 * Details about a linehaul region, which is a location group code.
 */
class LocationGroupCode {
    constructor() {
        /**
         * The code associated with the region.
         * Required: false
         * Array: false
         */
        this.regionCd = undefined;
        /**
         * The list of service center identification codes that belong to that region.
         * Required: false
         * Array: true
         */
        this.sicCds = undefined;
    }
}
/**
 * Details about an active service center.
 */
class LocationSic {
    constructor() {
        /**
         * The service center identification code.
         * Required: true
         * Array: false
         */
        this.sicCd = undefined;
        /**
         * The code associated with the group that belongs to the service center.
         * Required: false
         * Array: false
         */
        this.groupCd = undefined;
        /**
         * The region that belongs to the service center.
         * Required: false
         * Array: false
         */
        this.region = undefined;
        /**
         * The ciity for service center.
         * Required: false
         * Array: false
         */
        this.city = undefined;
        /**
         * The offset of the service center.
         * Required: false
         * Array: false
         */
        this.serviceCenterOffset = undefined;
        /**
         * The state for the service center.
         * Required: false
         * Array: false
         */
        this.state = undefined;
        /**
         * Returns 1 if SIC is a FAC.
         * Required: false
         * Array: false
         */
        this.isFac = undefined;
        /**
         * Returns 1 if SIC is a OB.
         * Required: false
         * Array: false
         */
        this.isOb = undefined;
        /**
         * A description for the location.
         * Required: false
         * Array: false
         */
        this.sicDescription = undefined;
        /**
         * The SIC of the location hosting this satellite.
         * Required: false
         * Array: false
         */
        this.parentSatelliteSic = undefined;
        /**
         * The SIC of the service center that hosts the zone.
         * Required: false
         * Array: false
         */
        this.zoneTerminalSic = undefined;
        /**
         * Indicator to identify this SIC as a satellite.
         * Required: false
         * Array: false
         */
        this.aSatelliteSic = undefined;
        /**
         * Indicator to identify this SIC as a zone.
         * Required: false
         * Array: false
         */
        this.aZoneSic = undefined;
    }
}
/**
 * The operational hierarchy is an area by which the operations are segmented in the organization.
 */
class OperationalHierarchy {
    constructor() {
        /**
         * The service center identification code.
         * Required: false
         * Array: false
         */
        this.sicCd = undefined;
        /**
         * The category of the area. Example: Division.
         * Required: false
         * Array: false
         */
        this.sicCategory = undefined;
        /**
         * The Reference details about an area.
         * Required: false
         * Array: false
         */
        this.reference = undefined;
        /**
         * The list of the regions along with the SICs that belong to them.
         * Required: false
         * Array: true
         */
        this.regions = undefined;
    }
}
/**
 * Operational Region represents a geographical area by which the Operations are divided in the organization and is composed of Districts.
 */
class OperationalRegion {
    constructor() {
        /**
         * The code associated with the region.
         * Required: false
         * Array: false
         */
        this.regionCd = undefined;
        /**
         * The name of the operational region. E.g. Eastern, Mid-West, and Western.
         * Required: false
         * Array: false
         */
        this.regionName = undefined;
        /**
         * A list of the districts along with the SICs that are part of it.
         * Required: false
         * Array: true
         */
        this.districts = undefined;
    }
}
/**
 * Details about postal code and SIC.
 */
class PostalSic {
    constructor() {
        /**
         * The service center identification code.
         * Required: false
         * Array: false
         */
        this.sicCd = undefined;
        /**
         * The postal code associated with the SIC.
         * Required: false
         * Array: false
         */
        this.postalCd = undefined;
    }
}
/**
 * Details about postal code and SIC associations.
 */
class PostalSicAssignment {
    constructor() {
        /**
         * Location Id code
         * Required: false
         * Array: false
         */
        this.sicCd = undefined;
        /**
         * Postal Code associated with the Sic Code
         * Required: false
         * Array: false
         */
        this.postalCd = undefined;
        /**
         * US state code or CA province code
         * Required: false
         * Array: false
         */
        this.stateCd = undefined;
        /**
         * 2 character country code
         * Required: false
         * Array: false
         */
        this.countryCd = undefined;
        /**
         * If set to true then the Postal Code is a direct point
         * Required: false
         * Array: false
         */
        this.directPointInd = undefined;
        /**
         * If set to true then the Rapid Remote Service (RRS) is available
         * Required: false
         * Array: false
         */
        this.rrsServiceInd = undefined;
        /**
         * The service center identification code for the host location for the zone.
         * Required: false
         * Array: false
         */
        this.hostSicCd = undefined;
        /**
         * The distance in miles from the host SIC.
         * Required: false
         * Array: false
         */
        this.distanceFromHostSicMiles = undefined;
    }
}
/**
 * Details about a postal SIC and the services it offers.
 */
class PostalSicService {
    constructor() {
        /**
         * Details about a postalSic.
         * Required: false
         * Array: false
         */
        this.postalSic = undefined;
        /**
         * The list of eligible services for a postal SIC code.
         * Required: false
         * Array: true
         */
        this.eligibleServices = undefined;
    }
}
/**
 * Information about the transit time between origin and destination postal codes.
 */
class PostalTransitTime {
    constructor() {
        /**
         * The state code for the origin.
         * Required: false
         * Array: false
         */
        this.origStateCd = undefined;
        /**
         * The country code for the origin.
         * Required: false
         * Array: false
         */
        this.origCountryCd = undefined;
        /**
         * The postal code for the origin.
         * Required: false
         * Array: false
         */
        this.origPostalCd = undefined;
        /**
         * The identification code for the origin service center that is picking up the freight.
         * Required: false
         * Array: false
         */
        this.origSicCd = undefined;
        /**
         * The pickup date that was requested by the customer. If not provided, then it defaults to today's date.
         * Required: false
         * Array: false
         */
        this.requestedPkupDate = undefined;
        /**
         * The earliest date by which the freight should be picked up in order to meet the requested delivery date.
         * Required: false
         * Array: false
         */
        this.earliestPkupDate = undefined;
        /**
         * The latest date by which the freight should be picked up in order to meet the requested delivery date.
         * Required: false
         * Array: false
         */
        this.latestPkupDate = undefined;
        /**
         * Indicates whether the requested pickup date is a holiday or non-working day.
         * Required: false
         * Array: false
         */
        this.isPkupDateHoliday = undefined;
        /**
         * The state code for the destination.
         * Required: false
         * Array: false
         */
        this.destStateCd = undefined;
        /**
         * The country code for the destination.
         * Required: false
         * Array: false
         */
        this.destCountryCd = undefined;
        /**
         * The postal code for the destination.
         * Required: false
         * Array: false
         */
        this.destPostalCd = undefined;
        /**
         * The identification code for the destination service center that is delivering the freight.
         * Required: false
         * Array: false
         */
        this.destSicCd = undefined;
        /**
         * The delivery date that was requested by the customer.
         * Required: false
         * Array: false
         */
        this.requestedDlvrDate = undefined;
        /**
         * The estimated delivery date that was derived from the requested pickup date and transit days.
         * Required: false
         * Array: false
         */
        this.estdDlvrDate = undefined;
        /**
         * Indicates whether the requested delivery date is a holiday or non-working day.
         * Required: false
         * Array: false
         */
        this.isrqstdDeliveryDateHoliday = undefined;
        /**
         * The number of standard service transit (business) days (excluding weekend and holidays) that it takes to move from the origin to destination.
         * Required: false
         * Array: false
         */
        this.transitDays = undefined;
        /**
         * The industry average number of transit (business) days (excluding weekend and holidays) it takes to move from the origin to destination.
         * Required: false
         * Array: false
         */
        this.transitDaysIndustryAvg = undefined;
        /**
         * The note details about the transit time.
         * Required: false
         * Array: false
         */
        this.note = undefined;
        /**
         * Indicates whether guaranteed service is available in this lane.
         * Required: false
         * Array: false
         */
        this.garntInd = undefined;
        /**
         * Indicates whether guaranteed by noon is available in this lane.
         * Required: false
         * Array: false
         */
        this.guaranteedByNoonInd = undefined;
        /**
         * Indicates whether Rapid Remote Service (RRS) is available in this lane.
         * Required: false
         * Array: false
         */
        this.rrsInd = undefined;
        /**
         * The RRS delivery date which was derived from the requested pickup date and transit days.
         * Required: false
         * Array: false
         */
        this.rrsEstimatedDeliveryDate = undefined;
        /**
         * The number of RRS transit (business) days (excluding weekend and holidays) that it takes to move from the origin to destination.
         * Required: false
         * Array: false
         */
        this.rrsTransitDays = undefined;
    }
}
/**
 * The region hierarchy represents a collection of service center identification codes under a region in the hierarchy. E.g. PACIFIC NW REGION.
 */
class RegionHierarchy {
    constructor() {
        /**
         * The service center identification code.
         * Required: false
         * Array: false
         */
        this.sicCd = undefined;
        /**
         * The category of the region in the hierarchy. E.g. OPS-REGION.
         * Required: false
         * Array: false
         */
        this.sicCategory = undefined;
        /**
         * The Reference details about a region.
         * Required: false
         * Array: false
         */
        this.reference = undefined;
        /**
         * The list of host SICs which include the zones/satellites that are linked to them.
         * Required: false
         * Array: true
         */
        this.hostSics = undefined;
    }
}
/**
 * Details about a service center.
 */
class ServiceCenter {
    constructor() {
        /**
         * Address infomration for the service center.
         * Required: false
         * Array: false
         */
        this.locAddress = undefined;
        /**
         * The service center identification code for the location.
         * Required: false
         * Array: false
         */
        this.sicCd = undefined;
        /**
         * The category of the service center. E.g. Zone/Satellite.
         * Required: false
         * Array: false
         */
        this.sicCategory = undefined;
        /**
         * Reference details about a service center.
         * Required: false
         * Array: false
         */
        this.reference = undefined;
    }
}
/**
 * *** Legacy type which has been deprecated.  Do not use ***
 * Basic Information for calculating Transit times
 */
class TransitTimeBasic {
    constructor() {
        /**
         * The postal code for the origin.
         * Required: true
         * Array: false
         */
        this.origPostalCd = undefined;
        /**
         * The postal code for the destination.
         * Required: true
         * Array: false
         */
        this.destPostalCd = undefined;
        /**
         * The estimated pickup date. If it is not provided, then it will default to the current date.
         * Required: false
         * Array: false
         */
        this.requestedPkupDate = undefined;
        /**
         * The requested delivery date.
         * Required: false
         * Array: false
         */
        this.requestedDlvrDate = undefined;
        /**
         * Indicates whether shipment requires special handling to prevent freezing during harsh weather.
         * Required: false
         * Array: false
         */
        this.freezableInd = undefined;
        /**
         * Indicates whether the embargo is for the shipper (origin) or consignee (destination).
         * Required: false
         * Array: false
         */
        this.embargoPartyInd = undefined;
        /**
         * Indicates whether Rapid Remote Service (RRS) is required.
         * Required: false
         * Array: false
         */
        this.rrsInd = undefined;
    }
}
/**
 * Details about zone and satellite.
 */
class ZoneSatelliteInfo {
    constructor() {
        /**
         * The SIC code for the region.
         * Required: false
         * Array: false
         */
        this.regionSic = undefined;
        /**
         * The SIC code for the location.
         * Required: false
         * Array: false
         */
        this.locationSic = undefined;
        /**
         * The parent satellite.
         * Required: false
         * Array: false
         */
        this.satelliteParent = undefined;
        /**
         * The zone location of the terminal.
         * Required: false
         * Array: false
         */
        this.zoneTerminal = undefined;
    }
}
/**
 * Path parameters for get Ensemble status counts
 */
class GetEnsembleStatusCountsPath {
    constructor() {
        /**
         * Name of the queue
         * Required: true
         * Array: false
         */
        this.queueName = undefined;
        /**
         * From timestamp
         * Required: true
         * Array: false
         */
        this.fromTmst = undefined;
        /**
         * To timestamp
         * Required: true
         * Array: false
         */
        this.toTmst = undefined;
    }
}
/**
 * Response to the request to get Ensemble status counts
 */
class GetEnsembleStatusCountsResp {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.ensembleMetrics = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.queueMetrics = undefined;
    }
}
/**
 * Path parameters for get Ensemble status counts
 */
class GetQueuePendingCountPath {
    constructor() {
        /**
         * Name of the queue
         * Required: true
         * Array: false
         */
        this.queueName = undefined;
    }
}
/**
 * Response for getQueuePendingCount
 */
class GetQueuePendingCountResp {
    constructor() {
        /**
         * Number of messages in either ready or wait status. This count indicates the queue depth.
         * Required: false
         * Array: false
         */
        this.pendingCount = undefined;
    }
}
/**
 * Request to Restart execution of a Started or Not Started  Ensemble
 */
class RestartEnsemblePath {
    constructor() {
        /**
         * Name of the queue
         * Required: false
         * Array: false
         */
        this.queueName = undefined;
        /**
         * Unique identifier for the application workflow.
         * Required: false
         * Array: false
         */
        this.ensembleInstId = undefined;
    }
}
/**
 * Request to Restart execution of a Started or Not Started  Ensemble
 */
class RestartEnsembleRqst {
}
/**
 * Response to the Request to Restart execution of a Started or Not Started  Ensemble
 */
class RestartEnsembleResp {
    constructor() {
        /**
         * Ensemble that was Restarted.
         * Required: false
         * Array: false
         */
        this.ensembleRestartedInd = undefined;
    }
}
/**
 * Request to Resume execution of a Suspended ensemble
 */
class ResumeEnsemblePath {
    constructor() {
        /**
         * Name of the queue
         * Required: false
         * Array: false
         */
        this.queueName = undefined;
        /**
         * Unique identifier for the application workflow.
         * Required: false
         * Array: false
         */
        this.ensembleInstId = undefined;
    }
}
/**
 * Request to Resume execution of a Suspended ensemble
 */
class ResumeEnsembleRqst {
}
/**
 * Response to the Request to Resume execution of a Suspended ensemble
 */
class ResumeEnsembleResp {
    constructor() {
        /**
         * Ensemble that was Resumed
         * Required: false
         * Array: false
         */
        this.ensembleResumedInd = undefined;
    }
}
/**
 * Request to start ensemble.
 */
class StartMessageDrivenEnsembleRqst {
    constructor() {
        /**
         * Required: true
         * Array: false
         */
        this.queue = undefined;
        /**
         * Required: true
         * Array: false
         */
        this.messageId = undefined;
        /**
         * Required: true
         * Array: false
         */
        this.message = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.eventDataType = undefined;
    }
}
/**
 * Enter comments
 */
class StartMessageDrivenEnsembleResp {
    constructor() {
        /**
         * Instance ID of the ensemble that was started.
         * Required: false
         * Array: false
         */
        this.ensembleInstId = undefined;
    }
}
/**
 * Request to stop execution of an Ensemble
 */
class KillEnsembleRqst {
    constructor() {
        /**
         * Unique identifier for the ensemble.
         * Required: true
         * Array: true
         */
        this.ensembleInstIds = undefined;
    }
}
/**
 * Response to the request to stop execution of an Ensemble
 */
class KillEnsembleResp {
    constructor() {
        /**
         * Ensembles that were killed.
         * Required: false
         * Array: true
         */
        this.status = undefined;
    }
}
/**
 * Path parameters for stopQueue
 */
class StopQueuePath {
    constructor() {
        /**
         * Name of the queue to stop
         * Required: false
         * Array: false
         */
        this.queueName = undefined;
    }
}
/**
 * Path parameters for stopQueue
 */
class StopQueueWebhookPath {
    constructor() {
        /**
         * Name of the queue to stop
         * Required: false
         * Array: false
         */
        this.queueName = undefined;
    }
}
/**
 * Path parameters for stopQueue
 */
class StopQueueWebhookQuery {
    constructor() {
        /**
         * Authorization token
         * Required: false
         * Array: false
         */
        this.auth_token = undefined;
    }
}
/**
 * Path parameters for startQueue
 */
class StartQueuePath {
    constructor() {
        /**
         * Name of the queue to start
         * Required: false
         * Array: false
         */
        this.queueName = undefined;
    }
}
/**
 * Response for listQueues
 */
class ListQueuesResp {
    constructor() {
        /**
         * Name of the queue to start
         * Required: false
         * Array: true
         */
        this.queues = undefined;
    }
}
/**
 * Path parameters
 */
class ListExpiredMessagesPath {
    constructor() {
        /**
         * Name of the queue
         * Required: true
         * Array: false
         */
        this.queueName = undefined;
        /**
         * From enqueue timestamp
         * Required: true
         * Array: false
         */
        this.enqueueFromTmst = undefined;
        /**
         * Tom enqueue timestamp
         * Required: true
         * Array: false
         */
        this.enqueueToTmst = undefined;
    }
}
/**
 * Response
 */
class ListExpiredMessagesResp {
    constructor() {
        /**
         * Name of the queue
         * Required: false
         * Array: true
         */
        this.message = undefined;
    }
}
/**
 * Path parameters
 */
class DeleteExpiredMessagesPath {
    constructor() {
        /**
         * Name of the queue
         * Required: true
         * Array: false
         */
        this.queueName = undefined;
    }
}
/**
 * Path parameters
 */
class DeleteExpiredMessagesQuery {
    constructor() {
        /**
         * Id of the message to be deleted
         * Required: true
         * Array: true
         */
        this.messageId = undefined;
    }
}
/**
 * Response to the request to stop execution of an Ensemble
 */
class DeleteExpiredMessagesResp {
    constructor() {
        /**
         * Ensembles that were killed.
         * Required: false
         * Array: true
         */
        this.status = undefined;
    }
}
/**
 * Path parameters
 */
class ReplayMessagePath {
    constructor() {
        /**
         * Name of the queue
         * Required: true
         * Array: false
         */
        this.queueName = undefined;
        /**
         * ID of the message to replay. It needs to be in expired status
         * Required: true
         * Array: false
         */
        this.messageId = undefined;
    }
}
/**
 * Path parameters for get Ensemble status counts
 */
class GetPubSubStatusCountsPath {
    constructor() {
        /**
         * Name of the queue
         * Required: true
         * Array: false
         */
        this.subscriptionId = undefined;
        /**
         * From timestamp
         * Required: true
         * Array: false
         */
        this.fromTmst = undefined;
        /**
         * To timestamp
         * Required: true
         * Array: false
         */
        this.toTmst = undefined;
    }
}
/**
 * Response to the request to get Ensemble status counts
 */
class GetPubSubStatusCountsResp {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.pubsubMetrics = undefined;
    }
}
/**
 * Path parameters for startSubscription
 */
class StartSubscriptionPath {
    constructor() {
        /**
         * Id of the subscription to start
         * Required: false
         * Array: false
         */
        this.subscriptionId = undefined;
    }
}
/**
 * Path parameters for stopSubscription
 */
class StopSubscriptionPath {
    constructor() {
        /**
         * Id of the subscription to stop
         * Required: false
         * Array: false
         */
        this.subscriptionId = undefined;
    }
}
/**
 * Path parameters for get Undelivered Message counts
 */
class GetUndeliveredMessagesPath {
    constructor() {
        /**
         * Id of subscription
         * Required: true
         * Array: false
         */
        this.subscriptionId = undefined;
    }
}
/**
 * Response for getUndeliveredMessages
 */
class GetUndeliveredMessagesResp {
    constructor() {
        /**
         * Number of Undelivered Messages.
         * Required: false
         * Array: false
         */
        this.undeliveredCount = undefined;
    }
}
/**
 * Response for listSubscriptions
 */
class ListSubscriptionsResp {
    constructor() {
        /**
         * Name of the queue to start
         * Required: false
         * Array: true
         */
        this.subscriptions = undefined;
    }
}
/**
 * Path parameters for Get subscription count
 */
class GetSubscriptionPath {
    constructor() {
        /**
         * Subscription id
         * Required: true
         * Array: false
         */
        this.subscriptionId = undefined;
    }
}
/**
 * Returns status of a given subscription
 */
class GetSubscriptionResp {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.subscription = undefined;
    }
}
/**
 * Path parameters for startChannel
 */
class StartChannelPath {
    constructor() {
        /**
         * Name of the channel to start
         * Required: false
         * Array: false
         */
        this.channelName = undefined;
    }
}
/**
 * Path parameters for stopChannel
 */
class StopChannelPath {
    constructor() {
        /**
         * Name of the channel to stop
         * Required: false
         * Array: false
         */
        this.channelName = undefined;
    }
}
/**
 * Response for listChannels
 */
class ListChannelsResp {
    constructor() {
        /**
         * list of channels
         * Required: false
         * Array: true
         */
        this.channels = undefined;
    }
}
class MessageDrivenEnsemblePayload {
    constructor() {
        /**
         * The type of event. This will determine which ensemble to execute
         * Required: false
         * Array: false
         */
        this.eventType = undefined;
        /**
         * The event occurrence timestamp
         * Required: false
         * Array: false
         */
        this.eventTmst = undefined;
        /**
         * The event occurrence timestamp in microseconds
         * Required: false
         * Array: false
         */
        this.eventTmstMicro = undefined;
        /**
         * The correlation ID of the originating transaction
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey1 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey2 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey3 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey4 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey5 = undefined;
        /**
         * The event data. Use JSON format with previous and current values of all attributes that were changed by this event
         * Required: false
         * Array: false
         */
        this.eventData = undefined;
    }
}
class DynamicMessageDrivenPayload {
    constructor() {
        /**
         * The type of the payload
         * Required: true
         * Array: false
         */
        this.eventDataType = undefined;
        /**
         * The message payload
         * Required: true
         * Array: false
         */
        this.payload = undefined;
    }
}
class TransactionEventEnsemblePayload {
    constructor() {
        /**
         * The type of event. This will determine which ensemble to execute
         * Required: false
         * Array: false
         */
        this.eventType = undefined;
        /**
         * The event occurrence timestamp
         * Required: false
         * Array: false
         */
        this.eventTmst = undefined;
        /**
         * The event occurrence timestamp in microseconds
         * Required: false
         * Array: false
         */
        this.eventTmstMicro = undefined;
        /**
         * The correlation ID of the originating transaction
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey1 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey2 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey3 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey4 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey5 = undefined;
        /**
         * The event data. Use JSON format with previous and current values of all attributes that were changed by this event
         * Required: false
         * Array: false
         */
        this.eventData = undefined;
    }
}
/**
 * Status of a delete expired message request
 */
class DeleteExpiredMessageStatus {
    constructor() {
        /**
         * Unique identifier for the ensemble record.
         * Required: false
         * Array: false
         */
        this.messageId = undefined;
        /**
         * Indicates whether the ensemble was killed.
         * Required: false
         * Array: false
         */
        this.messageDeletedInd = undefined;
        /**
         * A status describing why the message could not be deleted. Populated if messageDeletedInd is false
         * Required: false
         * Array: false
         */
        this.failStatus = undefined;
    }
}
/**
 * Status of a kill ensemble request
 */
class KillEnsembleStatus {
    constructor() {
        /**
         * Unique identifier for the ensemble record.
         * Required: false
         * Array: false
         */
        this.ensembleInstId = undefined;
        /**
         * Indicates whether the ensemble was killed.
         * Required: false
         * Array: false
         */
        this.ensembleKilledInd = undefined;
        /**
         * A status describing why the ensemble could not be killed. Populated if ensembleKilledInd is false
         * Required: false
         * Array: false
         */
        this.failStatus = undefined;
    }
}
/**
 * Stores execution information for application workflows.
 */
class EnsembleStateSearchRecord {
    constructor() {
        /**
         * Unique identifier for the ensemble record.
         * Required: false
         * Array: false
         */
        this.instId = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState = undefined;
    }
}
class Payload {
    constructor() {
        /**
         * Required: true
         * Array: false
         */
        this.type = undefined;
        /**
         * Required: true
         * Array: false
         */
        this.value = undefined;
    }
}
class EnsembleState {
    constructor() {
        /**
         * Unique identifier for the ensemble record.
         * Required: false
         * Array: false
         */
        this.instId = undefined;
        /**
         * Name of the application workflow.
         * Required: false
         * Array: false
         */
        this.name = undefined;
        /**
         * A short description of the ensemble
         * Required: false
         * Array: false
         */
        this.desc = undefined;
        /**
         * Name of the queue publishing this message.
         * Required: false
         * Array: false
         */
        this.queueName = undefined;
        /**
         * Id of the message.
         * Required: false
         * Array: false
         */
        this.messageId = undefined;
        /**
         * The timestamp of when the trigger event was created.
         * Required: false
         * Array: false
         */
        this.eventTmst = undefined;
        /**
         * The timestamp of when the message was received by ensemble.
         * Required: false
         * Array: false
         */
        this.recvTmst = undefined;
        /**
         * The timestamp of when this ensemble was dispatched to the executor thread.
         * Required: false
         * Array: false
         */
        this.dispatchTmst = undefined;
        /**
         * The timestamp of when this ensemble processing started.
         * Required: false
         * Array: false
         */
        this.strtTmst = undefined;
        /**
         * The timestamp of when this ensemble processing ended.
         * Required: false
         * Array: false
         */
        this.endTmst = undefined;
        /**
         * Dequeue time in milliseconds. Populated for started ensembles, and is the difference between the received and event timestamps.
         * Required: false
         * Array: false
         */
        this.dequeueTime = undefined;
        /**
         * Dispatch time in milliseconds. Populated for started ensembles, and is the difference between the dispatch and received timestamps.
         * Required: false
         * Array: false
         */
        this.dispatchTime = undefined;
        /**
         * Wait time in milliseconds. Populated for started ensembles, and is the difference between the start and dispatch timestamps.
         * Required: false
         * Array: false
         */
        this.waitTime = undefined;
        /**
         * Time in milliseconds it took to process this ensemble from the time it was started. Populated for completed ensembles, and is the difference between the end and start timestamps.
         * Required: false
         * Array: false
         */
        this.processTime = undefined;
        /**
         * Total elapsed time in milliseconds to process this ensemble from the time the event was created. Populated for completed ensembles, and is the difference between the start and event timestamps.
         * Required: false
         * Array: false
         */
        this.totalTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.lstUpdtTmst = undefined;
        /**
         * Status of workflow execution.
         *
         * Valid values:
         * NOT_STARTED
         * STARTED
         * SUSPENDED
         * COMPLETED
         * Required: false
         * Array: false
         */
        this.statCd = undefined;
        /**
         * Status message for the workflow execution.  This could be an error message, stacktrace, success message, etc.
         * Required: false
         * Array: false
         */
        this.statMsg = undefined;
        /**
         * Same value for CORRELATION
         *
         * id is assigned to various rows from different tables that form a single business
         *
         * transactions.
         * In other words, this groups multiple rows across various tables for a single business
         *
         * transaction.
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Application key value like pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey1 = undefined;
        /**
         * Application key value like pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey2 = undefined;
        /**
         * Application key value like pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey3 = undefined;
        /**
         * Application key value like pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey4 = undefined;
        /**
         * Application key value like pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey5 = undefined;
        /**
         * Name of the host on which the ensemble was last processed
         * Required: false
         * Array: false
         */
        this.host = undefined;
        /**
         * Name of the thread on which the ensemble was last processed
         * Required: false
         * Array: false
         */
        this.thread = undefined;
        /**
         * Timestamp of when the ensemble started waiting for lock
         * Required: false
         * Array: false
         */
        this.lockWaitStartTmst = undefined;
        /**
         * Time spent on waiting for lock
         * Required: false
         * Array: false
         */
        this.elapsedLockWaitTime = undefined;
        /**
         * Number of times this ensemble was put on lock wait
         * Required: false
         * Array: false
         */
        this.lockWaitCount = undefined;
        /**
         * When the ensemble was paused
         * Required: false
         * Array: false
         */
        this.pauseStartTmst = undefined;
        /**
         * When the pause will expire
         * Required: false
         * Array: false
         */
        this.pauseExpiryTime = undefined;
        /**
         * Number of times this ensemble was put on pause
         * Required: false
         * Array: false
         */
        this.pauseCount = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.minuetStates = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.gotoEnsembleStates = undefined;
        /**
         * Payload that is input to the ensemble execution.
         * Required: false
         * Array: false
         */
        this.payload = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.executionContext = undefined;
    }
}
class NestedEnsembleState {
    constructor() {
        /**
         * Name of the application workflow.
         * Required: false
         * Array: false
         */
        this.name = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.minuetStates = undefined;
    }
}
class EnsembleExecutionContext {
    constructor() {
        /**
         * Name of the application workflow.
         * Required: false
         * Array: false
         */
        this.name = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.minuetPtr = undefined;
    }
}
/**
 * Stores execution information for the tasks that compose the application workflow.
 */
class MinuetState {
    constructor() {
        /**
         * Name of the task.
         * Required: false
         * Array: false
         */
        this.name = undefined;
        /**
         * Response payload that is output  from the task.
         * Required: false
         * Array: false
         */
        this.respPayload = undefined;
        /**
         * Datetime the task execution started
         * Required: false
         * Array: false
         */
        this.strtTmst = undefined;
        /**
         * Datetime the task execution ended
         * Required: false
         * Array: false
         */
        this.endTmst = undefined;
        /**
         * Time in milliseconds it took to process this minuet from the time it was started. Populated for completed minuets, and is the difference between the end and start timestamps.
         * Required: false
         * Array: false
         */
        this.processTime = undefined;
        /**
         * Number of times this minuet was retried
         * Required: false
         * Array: false
         */
        this.executionCount = undefined;
        /**
         * Status of task execution.
         *
         * Valid values:
         * STARTED
         * FAILED
         * COMPLETED
         * Required: false
         * Array: false
         */
        this.statCd = undefined;
        /**
         * Status message for the task.  This could be an error message, stacktrace, success message, etc.
         * Required: false
         * Array: false
         */
        this.statMsg = undefined;
    }
}
class EnsembleMetrics {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.notStartedCount = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.startedCount = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.completedCount = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.suspendedCount = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.failedCount = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.killedCount = undefined;
    }
}
class PubSubMetrics {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.sentMessageCount = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ackedMessageCount = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.expiredAckDeadLinesCount = undefined;
    }
}
class QueueMetrics {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.readyCount = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.waitCount = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.processedCount = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.expiredCount = undefined;
    }
}
class AuxMessagePublishers {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.name = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.queueTypeCd = undefined;
    }
}
class EnsembleConfiguration {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.component = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.mode = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shortName = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.name = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.sourceTable = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.payloadType = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.cacheEnabled = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.lockEnabled = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.retriggerEnabled = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.waitEnabled = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.rulesEngineEnabled = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.messagePublisherEnabled = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.auxMessagePublishers = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.fileAccessEnabled = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.synchronousResponseType = undefined;
    }
}
class Subscription {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.id = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.status = undefined;
    }
}
class Channel {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.name = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.status = undefined;
    }
}
class LTLEventLocation {
    constructor() {
        /**
         * The type of event. This will determine which ensemble to execute
         * Required: false
         * Array: false
         */
        this.eventType = undefined;
        /**
         * Contains a list of additional event types that are included in this event.
         * Required: false
         * Array: true
         */
        this.auxEventTypes = undefined;
        /**
         * The type of event. This will determine which ensemble to execute
         * Required: false
         * Array: false
         */
        this.eventTmst = undefined;
        /**
         * The name of component from which this event originated.
         * Required: false
         * Array: false
         */
        this.srcComponent = undefined;
        /**
         * The correlation ID of the originating transaction
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey1 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey2 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey3 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey4 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey5 = undefined;
        /**
         * A unique id for the event. If this is chunked, the eventId will help in combining chunks of the same message
         * Required: false
         * Array: false
         */
        this.eventId = undefined;
        /**
         * If there are a large number of payloads, the events can be chunked and published as smaller payloads. Set chunkInd to true if this is a chunk of a larger event message
         * Required: false
         * Array: false
         */
        this.chunkedInd = undefined;
        /**
         * If this is a chunked event message, specifies the sequence number of this instance
         * Required: false
         * Array: false
         */
        this.chunkSeqNbr = undefined;
        /**
         * If this is a chunked event message, specifies the total number of chunks
         * Required: false
         * Array: false
         */
        this.totalChunks = undefined;
        /**
         * The event data. Use JSON format with previous and current values of all attributes that were changed by this event
         * Required: false
         * Array: true
         */
        this.eventData = undefined;
    }
}
/**
 * An Update response from a BULK update to elastic
 */
class BulkUpdateResp {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this._index = undefined;
        /**
         * Required: false
         * Array: false
         */
        this._id = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.result = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.status = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.error = undefined;
    }
}
/**
 * Details of an error for an item
 */
class BulkUpdateItemError {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.type = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.reason = undefined;
    }
}
/**
 * An item in the bulk api response
 */
class BulkApiRespItem {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.update = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.index = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.create = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.delete = undefined;
    }
}
class EsAggregationFieldRqst {
    constructor() {
        /**
         * the name of the property to aggregate on (siteCode, employeeCode, etc.)
         * Required: false
         * Array: false
         */
        this.name = undefined;
        /**
         * aggregation type on that property. Options for field object: term, date, daterange, distinctCount. Options for subfield object: term, stats, date, ranges, distinctCount
         * Required: false
         * Array: false
         */
        this.type = undefined;
        /**
         * max number of rows to return
         * Required: false
         * Array: false
         */
        this.size = undefined;
        /**
         * second, minute, hour, day, week, month, quarter, year. Applies only when type is set to date
         * Required: false
         * Array: false
         */
        this.dateInterval = undefined;
        /**
         * min number of documents to filter date interval on. Applies only when type is set to date
         * Required: false
         * Array: false
         */
        this.minimumDocumentCount = undefined;
        /**
         * date range filter
         * Required: false
         * Array: true
         */
        this.ranges = undefined;
        /**
         * min number of documents to filter date interval on. Applies only when type is set to date
         * Required: false
         * Array: true
         */
        this.subFields = undefined;
    }
}
class EsAggregationDateRange {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.from = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.to = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.key = undefined;
    }
}
class EsDynamicPivotRqst {
    constructor() {
        /**
         * Required: false
         * Array: true
         */
        this.fields = undefined;
    }
}
class EsAggregationRqst {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.dynamicPivot = undefined;
    }
}
class EsAggregationFieldResp {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.key = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.date = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.from = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.fromAsString = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.to = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.toAsString = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.docCount = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.subFields = undefined;
    }
}
class EsAggregationResp {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.dynamicPivot = undefined;
    }
}
class EnsembleStateSearchFilterNot {
    constructor() {
        /**
         * Required: false
         * Array: true
         */
        this.and = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.or = undefined;
    }
}
class EnsembleStateSearchFilterOr {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.instId = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.instId_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_messageId = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_messageId_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_queueName = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_queueName_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_name = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_name_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_desc = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_desc_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_eventTmst = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_eventTmst_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_strtTmst = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_strtTmst_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_endTmst = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_endTmst_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_statCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_statCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_correlationId = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_correlationId_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey1 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey1_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey2 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey2_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey3 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey3_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey4 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey4_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey5 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey5_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_host = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_host_keyword = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.and = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.not = undefined;
        this.toJSON = function () {
            return {
                "instId": this.instId,
                "instId.keyword": this.instId_keyword,
                "ensembleState.messageId": this.ensembleState_messageId,
                "ensembleState.messageId.keyword": this.ensembleState_messageId_keyword,
                "ensembleState.queueName": this.ensembleState_queueName,
                "ensembleState.queueName.keyword": this.ensembleState_queueName_keyword,
                "ensembleState.name": this.ensembleState_name,
                "ensembleState.name.keyword": this.ensembleState_name_keyword,
                "ensembleState.desc": this.ensembleState_desc,
                "ensembleState.desc.keyword": this.ensembleState_desc_keyword,
                "ensembleState.eventTmst": this.ensembleState_eventTmst,
                "ensembleState.eventTmst.keyword": this.ensembleState_eventTmst_keyword,
                "ensembleState.strtTmst": this.ensembleState_strtTmst,
                "ensembleState.strtTmst.keyword": this.ensembleState_strtTmst_keyword,
                "ensembleState.endTmst": this.ensembleState_endTmst,
                "ensembleState.endTmst.keyword": this.ensembleState_endTmst_keyword,
                "ensembleState.statCd": this.ensembleState_statCd,
                "ensembleState.statCd.keyword": this.ensembleState_statCd_keyword,
                "ensembleState.correlationId": this.ensembleState_correlationId,
                "ensembleState.correlationId.keyword": this.ensembleState_correlationId_keyword,
                "ensembleState.businessKey1": this.ensembleState_businessKey1,
                "ensembleState.businessKey1.keyword": this.ensembleState_businessKey1_keyword,
                "ensembleState.businessKey2": this.ensembleState_businessKey2,
                "ensembleState.businessKey2.keyword": this.ensembleState_businessKey2_keyword,
                "ensembleState.businessKey3": this.ensembleState_businessKey3,
                "ensembleState.businessKey3.keyword": this.ensembleState_businessKey3_keyword,
                "ensembleState.businessKey4": this.ensembleState_businessKey4,
                "ensembleState.businessKey4.keyword": this.ensembleState_businessKey4_keyword,
                "ensembleState.businessKey5": this.ensembleState_businessKey5,
                "ensembleState.businessKey5.keyword": this.ensembleState_businessKey5_keyword,
                "ensembleState.host": this.ensembleState_host,
                "ensembleState.host.keyword": this.ensembleState_host_keyword,
                "and": this.and,
                "not": this.not
            };
        };
    }
}
class EnsembleStateSearchFilterAnd {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.instId = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.instId_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_messageId = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_messageId_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_queueName = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_queueName_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_name = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_name_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_desc = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_desc_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_eventTmst = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_eventTmst_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_strtTmst = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_strtTmst_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_endTmst = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_endTmst_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_statCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_statCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_correlationId = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_correlationId_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey1 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey1_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey2 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey2_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey3 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey3_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey4 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey4_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey5 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey5_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_host = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_host_keyword = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.or = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.not = undefined;
        this.toJSON = function () {
            return {
                "instId": this.instId,
                "instId.keyword": this.instId_keyword,
                "ensembleState.messageId": this.ensembleState_messageId,
                "ensembleState.messageId.keyword": this.ensembleState_messageId_keyword,
                "ensembleState.queueName": this.ensembleState_queueName,
                "ensembleState.queueName.keyword": this.ensembleState_queueName_keyword,
                "ensembleState.name": this.ensembleState_name,
                "ensembleState.name.keyword": this.ensembleState_name_keyword,
                "ensembleState.desc": this.ensembleState_desc,
                "ensembleState.desc.keyword": this.ensembleState_desc_keyword,
                "ensembleState.eventTmst": this.ensembleState_eventTmst,
                "ensembleState.eventTmst.keyword": this.ensembleState_eventTmst_keyword,
                "ensembleState.strtTmst": this.ensembleState_strtTmst,
                "ensembleState.strtTmst.keyword": this.ensembleState_strtTmst_keyword,
                "ensembleState.endTmst": this.ensembleState_endTmst,
                "ensembleState.endTmst.keyword": this.ensembleState_endTmst_keyword,
                "ensembleState.statCd": this.ensembleState_statCd,
                "ensembleState.statCd.keyword": this.ensembleState_statCd_keyword,
                "ensembleState.correlationId": this.ensembleState_correlationId,
                "ensembleState.correlationId.keyword": this.ensembleState_correlationId_keyword,
                "ensembleState.businessKey1": this.ensembleState_businessKey1,
                "ensembleState.businessKey1.keyword": this.ensembleState_businessKey1_keyword,
                "ensembleState.businessKey2": this.ensembleState_businessKey2,
                "ensembleState.businessKey2.keyword": this.ensembleState_businessKey2_keyword,
                "ensembleState.businessKey3": this.ensembleState_businessKey3,
                "ensembleState.businessKey3.keyword": this.ensembleState_businessKey3_keyword,
                "ensembleState.businessKey4": this.ensembleState_businessKey4,
                "ensembleState.businessKey4.keyword": this.ensembleState_businessKey4_keyword,
                "ensembleState.businessKey5": this.ensembleState_businessKey5,
                "ensembleState.businessKey5.keyword": this.ensembleState_businessKey5_keyword,
                "ensembleState.host": this.ensembleState_host,
                "ensembleState.host.keyword": this.ensembleState_host_keyword,
                "or": this.or,
                "not": this.not
            };
        };
    }
}
class EnsembleStateSearchFilter {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.q = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.b = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.instId = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.instId_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_messageId = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_messageId_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_queueName = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_queueName_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_name = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_name_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_desc = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_desc_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_eventTmst = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_eventTmst_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_strtTmst = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_strtTmst_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_endTmst = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_endTmst_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_statCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_statCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_correlationId = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_correlationId_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey1 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey1_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey2 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey2_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey3 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey3_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey4 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey4_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey5 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey5_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_host = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_host_keyword = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.and = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.or = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.not = undefined;
        this.toJSON = function () {
            return {
                "q": this.q,
                "b": this.b,
                "instId": this.instId,
                "instId.keyword": this.instId_keyword,
                "ensembleState.messageId": this.ensembleState_messageId,
                "ensembleState.messageId.keyword": this.ensembleState_messageId_keyword,
                "ensembleState.queueName": this.ensembleState_queueName,
                "ensembleState.queueName.keyword": this.ensembleState_queueName_keyword,
                "ensembleState.name": this.ensembleState_name,
                "ensembleState.name.keyword": this.ensembleState_name_keyword,
                "ensembleState.desc": this.ensembleState_desc,
                "ensembleState.desc.keyword": this.ensembleState_desc_keyword,
                "ensembleState.eventTmst": this.ensembleState_eventTmst,
                "ensembleState.eventTmst.keyword": this.ensembleState_eventTmst_keyword,
                "ensembleState.strtTmst": this.ensembleState_strtTmst,
                "ensembleState.strtTmst.keyword": this.ensembleState_strtTmst_keyword,
                "ensembleState.endTmst": this.ensembleState_endTmst,
                "ensembleState.endTmst.keyword": this.ensembleState_endTmst_keyword,
                "ensembleState.statCd": this.ensembleState_statCd,
                "ensembleState.statCd.keyword": this.ensembleState_statCd_keyword,
                "ensembleState.correlationId": this.ensembleState_correlationId,
                "ensembleState.correlationId.keyword": this.ensembleState_correlationId_keyword,
                "ensembleState.businessKey1": this.ensembleState_businessKey1,
                "ensembleState.businessKey1.keyword": this.ensembleState_businessKey1_keyword,
                "ensembleState.businessKey2": this.ensembleState_businessKey2,
                "ensembleState.businessKey2.keyword": this.ensembleState_businessKey2_keyword,
                "ensembleState.businessKey3": this.ensembleState_businessKey3,
                "ensembleState.businessKey3.keyword": this.ensembleState_businessKey3_keyword,
                "ensembleState.businessKey4": this.ensembleState_businessKey4,
                "ensembleState.businessKey4.keyword": this.ensembleState_businessKey4_keyword,
                "ensembleState.businessKey5": this.ensembleState_businessKey5,
                "ensembleState.businessKey5.keyword": this.ensembleState_businessKey5_keyword,
                "ensembleState.host": this.ensembleState_host,
                "ensembleState.host.keyword": this.ensembleState_host_keyword,
                "and": this.and,
                "or": this.or,
                "not": this.not
            };
        };
    }
}
/**
 * Path parameters
 */
class GetElasticDocumentEnsembleStatePath {
    constructor() {
        /**
         * Uniquely identify the elastic search document
         * Required: true
         * Array: false
         */
        this.instId = undefined;
    }
}
/**
 * Query parameters
 */
class GetElasticDocumentEnsembleStateQuery {
    constructor() {
        /**
         * Source filter
         * Required: false
         * Array: false
         */
        this._source = undefined;
        /**
         * Source include filter
         * Required: false
         * Array: false
         */
        this._source_includes = undefined;
        /**
         * Source exclude filter
         * Required: false
         * Array: false
         */
        this._source_excludes = undefined;
    }
}
/**
 * Response from getElasticDocument.
 */
class GetElasticDocumentEnsembleStateResp {
    constructor() {
        /**
         * The record if found
         * Required: false
         * Array: false
         */
        this.record = undefined;
    }
}
/**
 * Query parameters
 */
class GetElasticDocumentEnsembleStateCountQuery {
    constructor() {
        /**
         * Query filter
         * Required: false
         * Array: false
         */
        this.q = undefined;
    }
}
/**
 * Response from getElasticDocumentCount.
 */
class GetElasticDocumentEnsembleStateCountResp {
    constructor() {
        /**
         * The count of the documents
         * Required: false
         * Array: false
         */
        this.count = undefined;
    }
}
/**
 * Request message to update information for EnsembleState.
 */
class UpdateElasticDocumentEnsembleStateRqst {
    constructor() {
        /**
         * The record to upsert in elastic
         * Required: false
         * Array: false
         */
        this.doc = undefined;
        /**
         * Indicate if an upsert operation should be executed if the document was not found
         * Required: false
         * Array: false
         */
        this.doc_as_upsert = undefined;
    }
}
/**
 * Path parameters
 */
class UpdateElasticDocumentEnsembleStatePath {
    constructor() {
        /**
         * Uniquely identify the elastic search document
         * Required: true
         * Array: false
         */
        this.instId = undefined;
    }
}
/**
 * Response from updateElasticDocument.
 */
class UpdateElasticDocumentEnsembleStateResp {
    constructor() {
        /**
         * The result of the operation
         * Required: false
         * Array: false
         */
        this.result = undefined;
    }
}
/**
 * Response from bulkUpdateElasticDocument.
 */
class BulkUpdateElasticDocumentEnsembleStateResp {
    constructor() {
        /**
         * The time it took to process this request
         * Required: false
         * Array: false
         */
        this.took = undefined;
        /**
         * Indicates if there are any errors
         * Required: false
         * Array: false
         */
        this.errors = undefined;
        /**
         * Results of the bulk operations
         * Required: false
         * Array: true
         */
        this.items = undefined;
    }
}
/**
 * Path parameters
 */
class DeleteElasticDocumentEnsembleStatePath {
    constructor() {
        /**
         * Uniquely identify the elastic search document
         * Required: true
         * Array: false
         */
        this.instId = undefined;
    }
}
/**
 * Request message to delete information for EnsembleState.
 */
class DeleteElasticDocumentEnsembleStateResp {
    constructor() {
        /**
         * The result of the operation
         * Required: false
         * Array: false
         */
        this.result = undefined;
    }
}
/**
 * Request message to search information for ensemble-state-unfiltered.
 */
class SearchEnsembleStateUnfilteredRqst {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.pageNumber = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.sortExpressions = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.pageSize = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.fields = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.excludeFields = undefined;
        /**
         * Query for searching
         * Required: false
         * Array: false
         */
        this.filter = undefined;
    }
}
/**
 * Response to the request to search information for ensemble-state-unfiltered.
 */
class SearchEnsembleStateUnfilteredResp {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.header = undefined;
        /**
         * Search results
         * Required: false
         * Array: true
         */
        this.result = undefined;
    }
}
/**
 * Request message to export information for ensemble-state-unfiltered.
 */
class ExportEnsembleStateUnfilteredRqst {
    constructor() {
        /**
         * Required: false
         * Array: true
         */
        this.fields = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.fileType = undefined;
        /**
         * Query for exporting
         * Required: false
         * Array: false
         */
        this.filter = undefined;
    }
}
/**
 * Request for register filter
 */
class RegisterFilterEnsembleStateUnfilteredRqst {
    constructor() {
        /**
         * Id of the  user
         * Required: false
         * Array: false
         */
        this.userId = undefined;
        /**
         * Connection token
         * Required: false
         * Array: false
         */
        this.connectionToken = undefined;
        /**
         * The filter to register
         * Required: false
         * Array: false
         */
        this.filter = undefined;
    }
}
/**
 * Response for register filter
 */
class RegisterFilterEnsembleStateUnfilteredResp {
    constructor() {
        /**
         * Hash for the filter
         * Required: false
         * Array: false
         */
        this.filterHash = undefined;
        /**
         * Duration for the API execution
         * Required: false
         * Array: false
         */
        this.duration = undefined;
        /**
         * Number of registrations created
         * Required: false
         * Array: false
         */
        this.numberOfRegistrations = undefined;
    }
}
/**
 * Request for register
 */
class UnregisterFilterEnsembleStateUnfilteredRqst {
    constructor() {
        /**
         * Id of the  user. For future use.
         * Required: false
         * Array: false
         */
        this.userId = undefined;
        /**
         * Connection token
         * Required: false
         * Array: false
         */
        this.connectionToken = undefined;
        /**
         * The hash of the filter to unregister. Filter has is returned by registerFilter
         * Required: false
         * Array: false
         */
        this.filterHash = undefined;
    }
}
/**
 * Request for changes feed
 */
class ChangesFeedEnsembleStateUnfilteredRqst {
    constructor() {
        /**
         * A list of document Ids
         * Required: false
         * Array: true
         */
        this.changedDocumentIds = undefined;
        /**
         * A list of changed documents
         * Required: false
         * Array: true
         */
        this.changedDocuments = undefined;
    }
}
/**
 *
 * 				Request for list aggregations
 * 				To aggregate data by any field you need to specify field properties on top level and subfield properties on any deeper level of aggregation.
 *
 */
class ListAggregationsEnsembleStateUnfilteredRqst {
    constructor() {
        /**
         * Filter for aggregations
         * Required: false
         * Array: false
         */
        this.filter = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.pageSize = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.aggregations = undefined;
    }
}
/**
 * Response for list aggregations
 */
class ListAggregationsEnsembleStateUnfilteredResp {
    constructor() {
        /**
         * Aggregations
         * Required: false
         * Array: false
         */
        this.aggregations = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.resultCount = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.pageNumber = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.pageSize = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.duration = undefined;
    }
}
/**
 *
 */
class AreaAssignment {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.areaInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.rsnbleDisplayTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.deliveryRouteDepartTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.pupOnlyInd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class CompanyOperations {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.refSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.companyNameCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.menloOperationsType = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.rsiOperationsType = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.trkldOperationsType = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.cityOperationsFlag = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.clasSzCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.operations24HrFlag = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dcmntImageFlag = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.displayToFlag = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dpndtFreightType = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dpndtSicFlag = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dir = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.facFlag = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.facHrsOfOpnCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.freightOperationsFlag = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.freightOperationsType = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.freightServiceAreaFlag = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.freightServiceAreaType = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.fuelFlag = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.hstedSicFlag = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.intmdlType = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.loadedToFlag = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.liftFlag = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.lnhDayMeetFlag = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.lnhNightMeetFlag = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.rrSpurFlag = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.safetyClasSzCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.satlType = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.shipmentOriginDestinationFlag = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.serviceSatlType = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.termProdFlag = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.termStmsLocationFlag = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.tieFlag = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.workDoorCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.warmRoomFlag = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.yardMeetAndTurnFlag = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.freightLtlMovementFlag = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.freightLtlMaintFlag = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.maintTypeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.milemakerSplcNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.detailCapxtimestamp = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.replicationLastUpdateDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dmlDateTime = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class ConWayCorporation {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.cnwyCorpCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.instanceId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.corpName = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.altCorpCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.cmptCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.cmptName = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.countryCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.companyLogoCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.glCorpCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.scacCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sltCorp3CharName = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sobCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sltT60MajCorp = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.companyNameCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.activeFlag = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.detailCapxtimestamp = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.replicationLastUpdateDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dmlDateTime = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class Door {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.displaySeqNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.doorNbrTxt = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.hzntlCoordNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.vrtclCoordNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sectorId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.pupOnlyInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dtlCapxTimestamp = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.replLstUpdtTmst = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dmlTmst = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.parentAreaInstId = undefined;
    }
}
/**
 *
 */
class DpndtCityOperationsLnk {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.parntSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.childSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dpndtFreightServiceDayCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.detailCapxtimestamp = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.replicationLastUpdateDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dmlDateTime = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class FeatureSetting {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.instId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.typeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.featureCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.settingValue = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.settingValueType = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.employeeId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.deviceId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.detailCapxtimestamp = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.replicationLastUpdateDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dmlDateTime = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class FullZipA {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.originSic = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.zip6 = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.state = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.country = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.daysDiff = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.directPoint = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.rrsServiceInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.distanceFromHostSicMl = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class GroupMember {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.groupCategoryCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.groupCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.refSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.effectiveDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.expiryDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.detailCapxtimestamp = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.replicationLastUpdateDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dmlDateTime = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class HierarchyCategory {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.hrchyCategoryCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.hrchyCategoryName = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.detailCapxtimestamp = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.replicationLastUpdateDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dmlDateTime = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class HierarchyGroup {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.hrchyGroupRefSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.hrchyGroupName = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sltAreaCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sltNameA = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sltNameB = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sltRgnCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.detailCapxtimestamp = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.replicationLastUpdateDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dmlDateTime = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.parentHrchyCategoryCd = undefined;
    }
}
/**
 *
 */
class HrchyGroupMbr {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.hrchyGroupRefSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.refSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.detailCapxtimestamp = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.replicationLastUpdateDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dmlDateTime = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class LoadPlan {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.destinationSic = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.currentSic = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.serviceTypeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.shiftCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.firstFacSic = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.mustClearSic = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.secondFacSic = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.thirdFacSic = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dayFreightInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.originRailSic = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.eslInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.destinationPowerSic = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.roomSic = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.thruSic = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.meetSic = undefined;
    }
}
/**
 *
 */
class LocFreightPostalArea {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.refSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.countryCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.countrySubdivisionCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.geoPostalCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.freightPostalAreaEffDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.activeFlag = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dirFlag = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.expiryDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.detailCapxtimestamp = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.replicationLastUpdateDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dmlDateTime = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.hostSicCd = undefined;
    }
}
/**
 * This table is used to identify the coordinates that create an area boundary within a service center. Each service center will have one or more area (like DTN, BVO, HIL) Added 06/23/2022  LISG-16350
 */
class LocGeoArea {
    constructor() {
        /**
         * Unique Primary key of the geo area
         * Required: false
         * Array: false
         */
        this.areaId = undefined;
        /**
         * Name given to the area
         * Required: false
         * Array: false
         */
        this.areaName = undefined;
        /**
         * The type of the geo area.
         *
         * Valid Values:
         * AREA
         * BNDRY
         * SATLT
         * Required: false
         * Array: false
         */
        this.typeCd = undefined;
        /**
         * Shape of the area
         * Required: false
         * Array: false
         */
        this.areaGeojson = undefined;
        /**
         * Brief description of the area
         * Required: false
         * Array: false
         */
        this.areaDescription = undefined;
        /**
         * Application audit field
         * Required: false
         * Array: false
         */
        this.reconciledDateTime = undefined;
        /**
         * Same value for CORRELATION id is assigned to various rows from different tables that form a single business transactions.
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
        /**
         * SIC Code to which the geo area belongs to
         * Required: false
         * Array: false
         */
        this.parentRefSicCd = undefined;
    }
}
/**
 * Holds a snapshot of LocGeoArea. Contains the details at the time on an event. It also provides the previous values if an update occured
 * This table is used to identify the coordinates that create an area boundary within a service center. Each service center will have one or more area (like DTN, BVO, HIL) Added 06/23/2022  LISG-16350
 */
class LocGeoAreaSnapshot {
    constructor() {
        /**
         * The action that created this snapshot. Add, Update or Delete
         * Required: false
         * Array: false
         */
        this.eventActionCd = undefined;
        /**
         * Unique Primary key of the geo area
         * Required: false
         * Array: false
         */
        this.areaId = undefined;
        /**
         * The previous snapshot, before this record was updated.
         * Required: false
         * Array: false
         */
        this.before = undefined;
        /**
         * The current snapshot.
         * Required: false
         * Array: false
         */
        this.after = undefined;
    }
}
/**
 *
 */
class LocOperationsSvccProfitability {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.sicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.cityOperationsInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.autoprtPdManifestCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.outboundAvgLoadedWeightLbs = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.outboundManPowerLbs = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.outboundPpldLbs = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.outboundPpldCubePercentage = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.prideStudyInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.pickupConfrmInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.routeVolumeThrshLbs = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.laserPrtr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.linePrtr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.autoprtObManifestCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.autoprtDsrLastCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.loadPlanInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.smart4RolloutDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sctrBalInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.ssrFacInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.ssrFacRolloutDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.ssrInboundInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.ssrInboundRolloutDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.autoptDsrTripCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.mimsOperationsInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.mimsRolloutDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dockOperationsInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dockOperationsRolloutDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.inboundArrInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.inboundArrRolloutDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.avgOutboundDensity = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.avgOutboundPceVolumeCubicFeet = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.detailCapxtimestamp = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.replicationLastUpdateDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dmlDateTime = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class LocationAddress {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.refSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.addressId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.locationAddressEffDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.altAddressType = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.addr1 = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.addr2 = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.addr3 = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.boxNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.cityName = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.countryCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.countryName = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.countrySubdivisionCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.countrySubdivisionName = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.companyName = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.deliveryInstructionDescription = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.expiryDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.nm1Name = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.nm2Name = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.prmyAddressFlag = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.postalCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.postalExtCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.expiryReasonCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.pyrlCntr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.pyrlCorpCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.pyrlEnvs = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.geoCodedFlag = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.postalCertifiedFlag = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.detailCapxtimestamp = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.replicationLastUpdateDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dmlDateTime = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 * Holds a snapshot of LocationAddress. Contains the details at the time on an event. It also provides the previous values if an update occured
 * null
 */
class LocationAddressSnapshot {
    constructor() {
        /**
         * The action that created this snapshot. Add, Update or Delete
         * Required: false
         * Array: false
         */
        this.eventActionCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.refSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.addressId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.locationAddressEffDate = undefined;
        /**
         * The previous snapshot, before this record was updated.
         * Required: false
         * Array: false
         */
        this.before = undefined;
        /**
         * The current snapshot.
         * Required: false
         * Array: false
         */
        this.after = undefined;
    }
}
/**
 *
 */
class LocationNonWeekDay {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.instanceId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.nonWorkDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.businessUnitCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.countryCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.stateCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.typeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.description = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.linehaulInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.revenueAllocationNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.pndStatusCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.detailCapxtimestamp = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.replicationLastUpdateDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dmlDateTime = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class LocationOperationalCalendar {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.calendarDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.revenueDayCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.revenueAllocationNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.caNonWeekInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.usNonWeekInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.financialReportDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.detailCapxtimestamp = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.replicationLastUpdateDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dmlDateTime = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class LocationReference {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.refSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.instanceId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sicName = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.corpOfcFlag = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dstFlag = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.effDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.expiryDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.finSicFlag = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.finFuncCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.finScndFuncCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.hrchyGroupFlag = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.operationsBgnDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.operationsEndDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.pdtTimeOffset = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.physLocationType = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.prvtFlag = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.refSicScpType = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sltPrimarySequence = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sltTermAbbrvName = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.kronFlag = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.lseExpiryDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.ownOrLseCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sltCountryCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sltStateAbbrv = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.pdtTimeOffsetInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.slsSicFlag = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.timezoneName = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.mrcCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.scanDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.scanLocationCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.scanLocationFlag = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.trkldCategoryCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.trkldXrefCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.mileageFlag = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.areaSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.rgnSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.safetyClasSzCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.physSiteLatd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.physSiteLngt = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.erpCompanyCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.erpProfitCenterCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.erpDepartmentCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.detailCapxtimestamp = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.replicationLastUpdateDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dmlDateTime = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 * Holds a snapshot of LocationReference. Contains the details at the time on an event. It also provides the previous values if an update occured
 * null
 */
class LocationReferenceSnapshot {
    constructor() {
        /**
         * The action that created this snapshot. Add, Update or Delete
         * Required: false
         * Array: false
         */
        this.eventActionCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.refSicCd = undefined;
        /**
         * The previous snapshot, before this record was updated.
         * Required: false
         * Array: false
         */
        this.before = undefined;
        /**
         * The current snapshot.
         * Required: false
         * Array: false
         */
        this.after = undefined;
    }
}
/**
 *
 */
class MexicoTransitTime {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.originCityName = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.destinationCityName = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.effectiveDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.terminalName = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.transitDaysNbr = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class NtdTransitTime {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.originZipPrefix = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.destinationZipPrefix = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.effectiveDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.avgType = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.avgYear = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.avgPeriod = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.avgTransitDays = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.nbrOfShipments = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.originCountryCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.destinationCountryCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class OffshorePort {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.mainlandSic = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.effectiveDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.expirationDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.portSic = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class SalesRegionSic {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.sicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.regionSicInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.lastUpdateTransactionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class SalesTerritory {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.salesTerritoryCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.instanceId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.activeInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.lastUpdateTransaction = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.excludeInReportInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.salesTerritoryDescription = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class SalesTerritoryGroup {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.territoryGroupCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.instanceId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.territoryGroupSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.groupTypeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.parentInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.racfUserid = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.activeInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.lastUpdateTransaction = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 * SIC to SIC transit days.
 */
class ServiceStdsA {
    constructor() {
        /**
         * Pickeup Service Center.
         * Required: false
         * Array: false
         */
        this.originSic = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.destinationSic = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.serviceTypeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.effectiveDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.expiryDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.transitDays = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.freightAssemblyCenter = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.originCarrier = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.destinationCarrier = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.eslLaneInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.transitLowDays = undefined;
    }
}
/**
 *
 */
class Sic {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.scac = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sic = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.termName = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.phoneNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.createTimestamp = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.detailCapxtimestamp = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.replicationLastUpdateDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dmlDateTime = undefined;
    }
}
/**
 *
 */
class ZipSic {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.zipCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.effectiveDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.oldSicCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class EventWait {
    constructor() {
        /**
         * Unique identifier of records in this table. Values populated from sequence
         * Required: false
         * Array: false
         */
        this.waitInstanceId = undefined;
        /**
         * Id of the ensemble that is being put on lock wait
         * Required: false
         * Array: false
         */
        this.ensembleInstanceId = undefined;
        /**
         * Name of the queue that was the source of this ensemble message
         * Required: false
         * Array: false
         */
        this.queueName = undefined;
        /**
         * The timestamp of when this ensemble was first put on lock wait
         * Required: false
         * Array: false
         */
        this.waitStartTmst = undefined;
        /**
         * The number of times this ensemble was put on lock wait
         * Required: false
         * Array: false
         */
        this.waitCount = undefined;
        /**
         * The duration of wait in milliseconds
         * Required: false
         * Array: false
         */
        this.waitTime = undefined;
        /**
         * The maximum wait time in milliseconds before the lock will be overridden
         * Required: false
         * Array: false
         */
        this.maxWaitTime = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class RetriggerEvent {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.eventRetriggerId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.regionCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceTableName = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.queueName = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.transactionId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.businessKey1 = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.businessKey2 = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.businessKey3 = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.businessKey4 = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.businessKey5 = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 * Details of a symphony message
 */
class SymphonyMessage {
    constructor() {
        /**
         * ID of the message
         * Required: false
         * Array: false
         */
        this.messageId = undefined;
        /**
         * Timestamp of when the message was enqueued
         * Required: false
         * Array: false
         */
        this.enqueueTmst = undefined;
        /**
         * Number of time the message was retried
         * Required: false
         * Array: false
         */
        this.retryCount = undefined;
        /**
         * Message payload
         * Required: false
         * Array: false
         */
        this.payload = undefined;
    }
}
/**
 * Details about a message queue. E.g. Oracle Advanced Queue
 */
class SymphonyQueue {
    constructor() {
        /**
         * Name of the queue
         * Required: false
         * Array: false
         */
        this.name = undefined;
        /**
         * Maximum number of retries
         * Required: false
         * Array: false
         */
        this.maxRetries = undefined;
        /**
         * Delay for retries
         * Required: false
         * Array: false
         */
        this.retryDelay = undefined;
        /**
         * Indicates if enqueue is enabled
         * Required: false
         * Array: false
         */
        this.enqueueEnabled = undefined;
        /**
         * Indicates if dequeue is enabled
         * Required: false
         * Array: false
         */
        this.dequeueEnabled = undefined;
        /**
         * The retention time
         * Required: false
         * Array: false
         */
        this.retention = undefined;
    }
}
/**
 * Captures transaction events. API writes one record per transaction.  Added to EVT seed model  06/26/2020
 */
class TransactionEvent {
    constructor() {
        /**
         * PK. Unique ID of the transaction. For Oracle, use the transaction id available in the context. Populated by trigger.
         * Required: false
         * Array: false
         */
        this.transactionId = undefined;
        /**
         * Identifies the type of transaction. Could be method name
         * Required: false
         * Array: false
         */
        this.transactionType = undefined;
        /**
         * Same value for CORRELATION id is assigned to various rows from different tables that form a single business transactions.
         * In other words, this groups multiple rows across various tables for a single business transaction.
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
        /**
         * Application key value like
         *
         * trip_inst_id, route_inst_id, shp_inst_id etc.
         * Required: false
         * Array: false
         */
        this.businessKey1 = undefined;
        /**
         * Application key value like
         *
         * trip_inst_id, route_inst_id, shp_inst_id etc.
         * Required: false
         * Array: false
         */
        this.businessKey2 = undefined;
        /**
         * Application key value like
         *
         * trip_inst_id, route_inst_id, shp_inst_id etc.
         * Required: false
         * Array: false
         */
        this.businessKey3 = undefined;
        /**
         * Application key value like
         *
         * trip_inst_id, route_inst_id, shp_inst_id etc.
         * Required: false
         * Array: false
         */
        this.businessKey4 = undefined;
        /**
         * Application key value like
         *
         * trip_inst_id, route_inst_id, shp_inst_id etc.
         * Required: false
         * Array: false
         */
        this.businessKey5 = undefined;
        /**
         * Number of seconds to hold event before publishing.  Added 09/21/2020  LISG-7498
         * Required: false
         * Array: false
         */
        this.eventReleaseDelaySeconds = undefined;
        /**
         *
         * Required: false
         * Array: true
         */
        this.transactionDetails = undefined;
        /**
         *
         * Required: false
         * Array: true
         */
        this.transactionBusinessEvents = undefined;
    }
}
/**
 *
 */
class TransactionEventBusiness {
    constructor() {
        /**
         * PK. Unique identifier for the record.  This will be populate by a trigger before insert to get the ID from EVT_TRANSACTION_BUS_SEQ
         * Required: false
         * Array: false
         */
        this.instId = undefined;
        /**
         * The transaction id from the session.  This will be populate by a trigger before insert.
         * Required: false
         * Array: false
         */
        this.transactionId = undefined;
        /**
         * Short description of the business transaction type.
         * Required: false
         * Array: false
         */
        this.transactionType = undefined;
        /**
         * Code identifying the type of business event.
         * Required: false
         * Array: false
         */
        this.typeCd = undefined;
        /**
         * Code identifying the type of business event.
         * Required: false
         * Array: false
         */
        this.subTypeCd = undefined;
        /**
         * Same value for CORRELATION id is assigned to various rows from different tables that form a single business transactions. In other words, this groups multiple rows across various tables for a single business transaction.
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 * Stores the snapshot of all the tables that were updated as part of the transaction. The snapshot is a json representation of all columns that were changed. The snapshot will be created by insert/update/delete triggers on the source table.   Added to EVT seed model  06/26/2020
 */
class TransactionEventDetail {
    constructor() {
        /**
         * PK. Unique id for the snapshot record. Generated by Sequence.
         * Required: false
         * Array: false
         */
        this.detailId = undefined;
        /**
         * Unique ID of the transaction. For Oracle, use the transaction id available in the context. Populated by trigger.
         * Required: false
         * Array: false
         */
        this.transactionId = undefined;
        /**
         * Name of the table for which the snapshot is stored
         * Required: false
         * Array: false
         */
        this.tableName = undefined;
        /**
         * Json string containing the before and after values for all the columns that were changed by the transaction.
         * Required: false
         * Array: false
         */
        this.snapshot = undefined;
        /**
         * Application key value like
         *
         * trip_inst_id, route_inst_id, shp_inst_id etc.
         * Required: false
         * Array: false
         */
        this.businessKey1 = undefined;
        /**
         * Application key value like
         *
         * trip_inst_id, route_inst_id, shp_inst_id etc.
         * Required: false
         * Array: false
         */
        this.businessKey2 = undefined;
        /**
         * Application key value like
         *
         * trip_inst_id, route_inst_id, shp_inst_id etc.
         * Required: false
         * Array: false
         */
        this.businessKey3 = undefined;
        /**
         * Application key value like
         *
         * trip_inst_id, route_inst_id, shp_inst_id etc.
         * Required: false
         * Array: false
         */
        this.businessKey4 = undefined;
        /**
         * Application key value like
         *
         * trip_inst_id, route_inst_id, shp_inst_id etc.
         * Required: false
         * Array: false
         */
        this.businessKey5 = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
        /**
         * Identifies the type of transaction. Could be method name
         * Required: false
         * Array: false
         */
        this.transactionType = undefined;
    }
}
/**
 * Enter comments
 */
class ListEnsembleConfigurationsResp {
    constructor() {
        /**
         * List of message queues - AQ and JMS
         * Required: false
         * Array: true
         */
        this.queues = undefined;
        /**
         * List of subscribers - Pub/Sub
         * Required: false
         * Array: true
         */
        this.subscriptions = undefined;
        /**
         * List of API channels
         * Required: false
         * Array: true
         */
        this.apiChannels = undefined;
        /**
         * List of API channels
         * Required: false
         * Array: true
         */
        this.timers = undefined;
        /**
         * List of API channels
         * Required: false
         * Array: true
         */
        this.emails = undefined;
        /**
         * List of API channels
         * Required: false
         * Array: true
         */
        this.files = undefined;
        /**
         * List of API channels
         * Required: false
         * Array: true
         */
        this.jmsQueues = undefined;
    }
}
class TimerConfig {
    constructor() {
        /**
         * Name of the timer.
         * Required: true
         * Array: false
         */
        this.name = undefined;
        /**
         * Interval for timer. Specify in seconds. The value is set in Octopus using the property sym.timer.<name>.intervalInSeconds
         * Required: true
         * Array: false
         */
        this.intervalInSeconds = undefined;
        /**
         * Specify the timer behavior. If fixedRate is set to true, the timer will fire an ensemble once every interval regardless of whether previous tasks are executing.
         * 						If fixedRate is set to false, the timer will be scheduled to fire after the specified interval, once the current execution is complete.
         * 						For both settings, it does not matter whether the ensemble execution is successful or not for the next timer event to be fired.
         * Required: true
         * Array: false
         */
        this.fixedRate = undefined;
        /**
         * An expression similar to crontab. Expression = 'minute hour dayOfMonth month dayOfWeek'.
         * 						Minute = 0-59, hour = 0-23, dayOfMonth = 1-31, month = [1-12 or JAN, FEB, ..., DEC],  dayOfWeek = [0-6 OR SUN-SAT].
         * 						[*] = any values, [,] = list of values, [-] = range of values, [/] = step values
         * Required: true
         * Array: false
         */
        this.expression = undefined;
    }
}
class Timer {
    constructor() {
        /**
         * Configuration of the timer.
         * Required: true
         * Array: false
         */
        this.configuration = undefined;
        /**
         * Indicates whether the timer is running. True indicates timer is running
         * Required: true
         * Array: false
         */
        this.runningInd = undefined;
    }
}
/**
 * Path parameters for listTimerEnsembles
 */
class ListTimerEnsemblesPath {
    constructor() {
        /**
         * The name of the timer ensemble from which the list of timers is requested
         * Required: false
         * Array: false
         */
        this.name = undefined;
    }
}
/**
 * Response for listTimerEnsembles
 */
class ListTimerEnsemblesResp {
    constructor() {
        /**
         * List of timers defined in this ensemble.
         * Required: false
         * Array: true
         */
        this.timers = undefined;
    }
}
/**
 * Path parameters for startTimerEnsemble
 */
class StartTimerEnsemblePath {
    constructor() {
        /**
         * The name of the timer to start
         * Required: false
         * Array: false
         */
        this.name = undefined;
    }
}
/**
 * Response for startTimerEnsemble
 */
class StartTimerEnsembleResp {
    constructor() {
        /**
         * Indicates whether the timer is running. True indicates timer is running
         * Required: false
         * Array: false
         */
        this.runningInd = undefined;
    }
}
/**
 * Path parameters for stopTimerEnsemble
 */
class StopTimerEnsemblePath {
    constructor() {
        /**
         * The name of the timer to stop
         * Required: false
         * Array: false
         */
        this.name = undefined;
    }
}
/**
 * Response for stopTimerEnsemble
 */
class StopTimerEnsembleResp {
    constructor() {
        /**
         * Indicates whether the timer is running. True indicates timer is running
         * Required: false
         * Array: false
         */
        this.runningInd = undefined;
    }
}
/**
 * Payload for processing carrier management transaction events.
 */
class LocationEventPayload extends PayloadType {
    constructor() {
        super(...arguments);
        /**
         * Snapshots of the records modified by the transaction event for which this payload was published.
         * Required: true
         * Array: true
         */
        this.snapshots = undefined;
        /**
         * The associated list of LocationAddress at the time when the event was published.
         * Required: true
         * Array: true
         */
        this.locationAddressSnapshots = undefined;
        /**
         * The associated list of GeoArea at the time when the event was published.
         * Required: true
         * Array: true
         */
        this.geoAreaSnapshots = undefined;
    }
}
/**
 * This type will be used to publish documents targeted for Elastic Search on a Kafka topic. The type includes KafkaMessageHeader.
 */
class EnsembleStateSearchRecordMessage extends EnsembleStateSearchRecord {
    constructor() {
        super(...arguments);
        /**
         * Additional details about the document
         * Required: false
         * Array: false
         */
        this.messageHeader = undefined;
    }
}

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes} checked by tsc
 */

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes} checked by tsc
 */

export { SdkLocationModule, LocationApiService, CalculateTransitTimeRqst, CalculateTransitTimeResp, DeleteLocationFeatureSettingsQuery, DetermineNonWorkDayPath, DetermineNonWorkDayResp, DetermineOperationalServiceDatePath, DetermineOperationalServiceDateQuery, DetermineOperationalServiceDateResp, GetGeoAreasForLocationPath, GetGeoAreasForLocationQuery, GetGeoAreasForLocationResp, GetMexicoTransitTimePath, GetMexicoTransitTimeResp, GetSalesTerritoryAndRegionSicQuery, GetSalesTerritoryAndRegionSicResp, GetLocationReferenceDetailsQuery, GetLocationReferenceDetailsResp, GetLocHierarchyGroupQuery, GetLocHierarchyGroupResp, GetLocOperationsServiceCenterProfitabilityBySicPath, GetLocOperationsServiceCenterProfitabilityBySicResp, GetOffshorePortBySicPath, GetOffshorePortBySicResp, GetOperationalServiceDaysCountPath, GetOperationalServiceDaysCountQuery, GetOperationalServiceDaysCountResp, GetRefSicAddressPath, GetRefSicAddressResp, GetRefSicHierarchyGroupNamesQuery, GetRefSicHierarchyGroupNamesResp, GetSectorByDoorNumberPath, GetSectorByDoorNumberResp, GetSicForPostalCodesQuery, GetSicForPostalCodesResp, ListAllActiveDoorsBySicPath, ListAllActiveDoorsBySicQuery, ListAllActiveDoorsBySicResp, ListAllActiveSicsRqst, ListAllActiveSicsResp, ListAllSicCdsByRegionPath, ListAllSicCdsByRegionResp, ListAreaAssignmentsQuery, ListAreaAssignmentsResp, ListCompanyOperationsQuery, ListCompanyOperationsResp, ListEligibleServicesByPostalSicRqst, ListEligibleServicesByPostalSicResp, ListGeoAreasPath, ListGeoAreasQuery, ListGeoAreasResp, ListLocationFeaturesQuery, ListLocationFeaturesResp, ListLocationGroupCodesQuery, ListLocationGroupCodesResp, ListLocationsQuery, ListLocationsRqst, ListLocationsResp, ListNonWorkDaysQuery, ListNonWorkDaysResp, ListOperationalHierarchyQuery, ListOperationalHierarchyResp, ListOperationalRegionsQuery, ListOperationalRegionsResp, ListOperationalServiceCentersQuery, ListOperationalServiceCentersResp, ListPostalAssignmentsForSicPath, ListPostalAssignmentsForSicResp, UpsertLocationFeatureSettingRqst, UpsertLocationFeatureSettingResp, GetHostSicDetailsPath, GetHostSicDetailsResp, GetLocReferenceDetailsBySicPath, GetLocReferenceDetailsBySicQuery, GetLocReferenceDetailsBySicResp, GetNextBusinessDayPath, GetNextBusinessDayQuery, GetNextBusinessDayResp, GetNextBusinessDayByCountryPath, GetNextBusinessDayByCountryQuery, GetNextBusinessDayByCountryResp, GetServiceCenterDetailsBySicPath, GetServiceCenterDetailsBySicResp, GetZoneAndSatelliteBySicPath, GetZoneAndSatelliteBySicQuery, GetZoneAndSatelliteBySicResp, ListLocationFeatureValuesByFeaturePath, ListLocationFeatureValuesByFeatureQuery, ListLocationFeatureValuesByFeatureResp, ListOperationalCalendarDaysQuery, ListOperationalCalendarDaysResp, UpsertGeoAreasPath, UpsertGeoAreasRqst, UpsertGeoAreasResp, ActiveSic, District, GeoArea, HostSic, LocationCompanyOperation, LocationGroupCode, LocationSic, OperationalHierarchy, OperationalRegion, PostalSic, PostalSicAssignment, PostalSicService, PostalTransitTime, RegionHierarchy, ServiceCenter, TransitTimeBasic, ZoneSatelliteInfo, GetEnsembleStatusCountsPath, GetEnsembleStatusCountsResp, GetQueuePendingCountPath, GetQueuePendingCountResp, RestartEnsemblePath, RestartEnsembleRqst, RestartEnsembleResp, ResumeEnsemblePath, ResumeEnsembleRqst, ResumeEnsembleResp, StartMessageDrivenEnsembleRqst, StartMessageDrivenEnsembleResp, KillEnsembleRqst, KillEnsembleResp, StopQueuePath, StopQueueWebhookPath, StopQueueWebhookQuery, StartQueuePath, ListQueuesResp, ListExpiredMessagesPath, ListExpiredMessagesResp, DeleteExpiredMessagesPath, DeleteExpiredMessagesQuery, DeleteExpiredMessagesResp, ReplayMessagePath, GetPubSubStatusCountsPath, GetPubSubStatusCountsResp, StartSubscriptionPath, StopSubscriptionPath, GetUndeliveredMessagesPath, GetUndeliveredMessagesResp, ListSubscriptionsResp, GetSubscriptionPath, GetSubscriptionResp, StartChannelPath, StopChannelPath, ListChannelsResp, MessageDrivenEnsemblePayload, DynamicMessageDrivenPayload, TransactionEventEnsemblePayload, DeleteExpiredMessageStatus, KillEnsembleStatus, EnsembleStateSearchRecord, Payload, EnsembleState, NestedEnsembleState, EnsembleExecutionContext, MinuetState, EnsembleMetrics, PubSubMetrics, QueueMetrics, AuxMessagePublishers, EnsembleConfiguration, Subscription, Channel, LTLEventLocation, BulkUpdateResp, BulkUpdateItemError, BulkApiRespItem, EsAggregationFieldRqst, EsAggregationDateRange, EsDynamicPivotRqst, EsAggregationRqst, EsAggregationFieldResp, EsAggregationResp, EnsembleStateSearchFilterNot, EnsembleStateSearchFilterOr, EnsembleStateSearchFilterAnd, EnsembleStateSearchFilter, GetElasticDocumentEnsembleStatePath, GetElasticDocumentEnsembleStateQuery, GetElasticDocumentEnsembleStateResp, GetElasticDocumentEnsembleStateCountQuery, GetElasticDocumentEnsembleStateCountResp, UpdateElasticDocumentEnsembleStateRqst, UpdateElasticDocumentEnsembleStatePath, UpdateElasticDocumentEnsembleStateResp, BulkUpdateElasticDocumentEnsembleStateResp, DeleteElasticDocumentEnsembleStatePath, DeleteElasticDocumentEnsembleStateResp, SearchEnsembleStateUnfilteredRqst, SearchEnsembleStateUnfilteredResp, ExportEnsembleStateUnfilteredRqst, RegisterFilterEnsembleStateUnfilteredRqst, RegisterFilterEnsembleStateUnfilteredResp, UnregisterFilterEnsembleStateUnfilteredRqst, ChangesFeedEnsembleStateUnfilteredRqst, ListAggregationsEnsembleStateUnfilteredRqst, ListAggregationsEnsembleStateUnfilteredResp, AreaAssignment, CompanyOperations, ConWayCorporation, Door, DpndtCityOperationsLnk, FeatureSetting, FullZipA, GroupMember, HierarchyCategory, HierarchyGroup, HrchyGroupMbr, LoadPlan, LocFreightPostalArea, LocGeoArea, LocGeoAreaSnapshot, LocOperationsSvccProfitability, LocationAddress, LocationAddressSnapshot, LocationNonWeekDay, LocationOperationalCalendar, LocationReference, LocationReferenceSnapshot, MexicoTransitTime, NtdTransitTime, OffshorePort, SalesRegionSic, SalesTerritory, SalesTerritoryGroup, ServiceStdsA, Sic, ZipSic, EventWait, RetriggerEvent, SymphonyMessage, SymphonyQueue, TransactionEvent, TransactionEventBusiness, TransactionEventDetail, ListEnsembleConfigurationsResp, TimerConfig, Timer, ListTimerEnsemblesPath, ListTimerEnsemblesResp, StartTimerEnsemblePath, StartTimerEnsembleResp, StopTimerEnsemblePath, StopTimerEnsembleResp, LocationEventPayload, EnsembleStateSearchRecordMessage };

